import React, { useEffect, useState, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
} from 'chart.js';
import { useParams } from 'react-router-dom';
import BackToDashboardButton from '../components/BackToDashboardButton';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  CircularProgress,
  Skeleton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Switch,
  Badge,
  Stack,
  Collapse,
  Snackbar,
  LinearProgress,
  ButtonBase,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarIcon,
  PhotoLibrary as PortfolioIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  AddCircleOutline as AddCircleIcon,
  CompareArrows as CompareIcon,
  Bolt as BoltIcon,
  Insights as InsightsIcon,
  Replay as ReplayIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  History as HistoryIcon,
  TipsAndUpdates as TipsIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Sync as SyncIcon,
  WarningAmber as WarnIcon,
  AutoAwesome as AutoAwesomeIcon,
  Telegram as TelegramIcon,
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  Add as AddIcon,
  NoteAlt as NoteAltIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CheckCircleIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  DoneAll as DoneAllIcon,
  ChatBubbleOutline as CommentIcon,
} from '@mui/icons-material';
import StudentTimeline from '../components/StudentTimeline';
import MultimediaPortfolio from '../components/MultimediaPortfolio';
import ProgressComparison from '../components/ProgressComparison';
import FlashcardAssessment from '../components/FlashcardAssessment';
import ShareAssessmentDialog from '../components/share/ShareAssessmentDialog';
import MemoryPanel from '../components/memory/MemoryPanel';
import IosShareIcon from '@mui/icons-material/IosShare';
import AttendanceImpact from '../components/AttendanceImpact';
import LessonNotes from '../components/LessonNotes';
import useStore from '../store/useStore';
import useIsMobile from '../hooks/useIsMobile';
import { translations } from '../translations';
import Layout from '../components/Layout';
import PdfExportPanel from '../components/PdfExportPanel';
import { serverFileName } from '../components/fileDownload';
import ProfileVideosSection from '../components/video/ProfileVideosSection';
import PersonPhotos from '../components/photos/PersonPhotos';
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  Filler
);

// --- Home Observations Sub-Component ---
function HomeObservations({ studentId, observations, isParent, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [itemName, setItemName] = useState('');
  const [parentNote, setParentNote] = useState('');
  const { language } = useStore();
  const t = translations[language];

  const handleSubmit = async () => {
    try {
      const res = await fetch(`/api/students/${studentId}/observations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemName, parentNote }),
      });
      if (res.ok) {
        setOpen(false);
        setItemName('');
        setParentNote('');
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Failed to save observation');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t.homeActivity}
        </Typography>
        {isParent && (
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => setOpen(true)}
          >
            {language === 'en' ? 'Record Home Use' : 'Записать результат'}
          </Button>
        )}
      </Box>
      {observations?.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}
        >
          <Typography color="text.secondary">
            {language === 'en'
              ? 'No home observations recorded yet.'
              : 'Домашних наблюдений пока нет.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {observations?.map((obs, idx) => (
            <Grid size={{ xs: 12 }} key={idx}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderLeft: '6px solid',
                  borderLeftColor: 'secondary.main',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: '16px !important' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {obs.itemName}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {new Date(obs.observedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: 'secondary.50',
                      borderRadius: 1,
                    }}
                  >
                    {obs.parentNote}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {language === 'en'
            ? `I heard ${itemName || 'something'} at home!`
            : `Ребенок сказал ${itemName || 'что-то'} дома!`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={
                language === 'en' ? 'What did you hear?' : 'Что вы услышали?'
              }
              placeholder="e.g. 'Apple'"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label={
                language === 'en' ? 'Context / Note' : 'Контекст / Заметка'
              }
              value={parentNote}
              onChange={(e) => setParentNote(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>
            {language === 'en' ? 'Cancel' : 'Отмена'}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!itemName}
          >
            {language === 'en' ? 'Save' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Smart Insights Sub-Component ---
// ─── Theme Growth Chart ───────────────────────────────────────────────────────
const CHART_COLORS = [
  '#C2233E',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
  '#ec4899',
  '#14b8a6',
  '#7A4988',
];

function ThemeGrowthChart({ studentId }) {
  const [themeData, setThemeData] = useState(null);
  const [open, setOpen] = useState(false);
  const { language } = useStore();
  const isRu = language === 'ru';

  useEffect(() => {
    if (!open) return;
    fetch(`/api/analytics/theme-growth/${studentId}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setThemeData(d.themes))
      .catch(() => {});
  }, [studentId, open]);

  const keys = themeData ? Object.keys(themeData) : [];
  if (!open) {
    return (
      <Box sx={{ mb: 2, textAlign: 'right' }}>
        <Button
          size="small"
          startIcon={<TipsIcon />}
          onClick={() => setOpen(true)}
          variant="outlined"
        >
          {isRu ? 'Рост по темам' : 'Theme Growth'}
        </Button>
      </Box>
    );
  }

  // Build unified date labels
  const allDates = [
    ...new Set(
      keys.flatMap((k) =>
        themeData[k].map((p) =>
          new Date(p.date).toLocaleDateString(isRu ? 'ru-RU' : 'en-GB', {
            day: '2-digit',
            month: 'short',
          })
        )
      )
    ),
  ];

  const datasets = keys.slice(0, 8).map((key, i) => {
    const points = themeData[key];
    const byDate = {};
    points.forEach((p) => {
      const label = new Date(p.date).toLocaleDateString(
        isRu ? 'ru-RU' : 'en-GB',
        { day: '2-digit', month: 'short' }
      );
      byDate[label] = p.mastery;
    });
    return {
      label: key.includes(' - ') ? key.split(' - ')[1] : key,
      data: allDates.map((d) => byDate[d] ?? null),
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + '22',
      tension: 0.3,
      spanGaps: true,
      pointRadius: 4,
    };
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <TipsIcon fontSize="small" color="primary" />
          {isRu ? 'Рост по темам' : 'Theme Growth'}
        </Typography>
        <Button size="small" onClick={() => setOpen(false)}>
          {isRu ? 'Свернуть' : 'Collapse'}
        </Button>
      </Box>
      {!themeData ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : keys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {isRu ? 'Нет данных' : 'No data yet'}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Line
            data={{ labels: allDates, datasets }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { boxWidth: 12, font: { size: 11 } },
                },
                tooltip: {
                  callbacks: { label: (c) => ` ${c.dataset.label}: ${c.raw}%` },
                },
              },
              scales: {
                y: {
                  min: 0,
                  max: 100,
                  ticks: { callback: (v) => `${v}%`, font: { size: 10 } },
                },
                x: { ticks: { font: { size: 10 } } },
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
}

// ─── Smart Insights ───────────────────────────────────────────────────────────
function SmartInsights({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useStore();

  useEffect(() => {
    fetch(`/api/analytics/insights/${studentId}?lang=${language}`, {
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId, language]);

  if (loading) return <CircularProgress size={24} />;
  if (!Array.isArray(data?.insights) || data.insights.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {data.insights.map((insight, i) => (
        <Alert
          key={i}
          severity={
            insight.type === 'alert'
              ? 'error'
              : insight.type === 'warning'
                ? 'warning'
                : 'success'
          }
          sx={{ mb: 1, borderRadius: 2 }}
          icon={<TipsIcon />}
        >
          {insight.text}
        </Alert>
      ))}
      {data.suggestions?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 1, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {language === 'en' ? 'Suggested Next Steps' : 'Рекомендации'}
          </Typography>
          {data.suggestions.slice(0, 3).map((s, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              {language === 'en'
                ? `Reassess "${s.themeName}" (${s.reason})`
                : `Переоценить "${s.themeName}" (${s.reason})`}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
}

// ─── AI "where to improve" insights ────────────────────────────────────────
function AiInsightsCard({ studentId }) {
  const { language } = useStore();
  const isRu = language !== 'en';
  const lang = isRu ? 'ru' : 'en';
  const [data, setData] = useState(null); // { _id, createdAt, language, insights }
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    fetch(`/api/ai/insights/student/${studentId}?language=${lang}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (res.status === 404) return null;
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
        return body;
      })
      .then((d) => !cancelled && setData(d?.insights ? d : null))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [studentId, lang]);

  async function generate() {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`/api/ai/insights/student/${studentId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: lang }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  function copyComment(text) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() =>
        setError(isRu ? 'Не удалось скопировать' : 'Could not copy')
      );
  }

  const ins = data?.insights;
  const sectionSx = {
    fontSize: '0.7rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'text.secondary',
    mb: 0.5,
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <AutoAwesomeIcon fontSize="small" color="secondary" />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 800, flex: 1, minWidth: 0 }}
        >
          {isRu ? 'Где улучшить (AI)' : 'Where to improve (AI)'}
        </Typography>
        {data?.createdAt && (
          <Typography variant="caption" color="text.secondary">
            {isRu ? 'Сгенерировано' : 'Generated'}{' '}
            {new Date(data.createdAt).toLocaleDateString(
              isRu ? 'ru-RU' : 'en-GB',
              { day: 'numeric', month: 'long' }
            )}
          </Typography>
        )}
        {data && (
          <Button
            size="small"
            startIcon={
              generating ? <CircularProgress size={14} /> : <RefreshIcon />
            }
            disabled={generating}
            onClick={generate}
          >
            {isRu ? 'Обновить' : 'Refresh'}
          </Button>
        )}
      </Box>

      {generating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {isRu ? 'AI анализирует… ~20 сек' : 'AI is analysing… ~20 s'}
          </Typography>
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={20} />
      ) : !ins ? (
        !generating && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1, minWidth: 200 }}
            >
              {isRu
                ? 'AI изучит оценки, посещаемость и заметки и подскажет, над чем поработать.'
                : 'AI reviews assessments, attendance and notes and suggests what to work on.'}
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="secondary"
              startIcon={<AutoAwesomeIcon />}
              onClick={generate}
            >
              {isRu ? 'Сгенерировать рекомендации' : 'Generate recommendations'}
            </Button>
          </Box>
        )
      ) : (
        <Box sx={{ opacity: generating ? 0.5 : 1 }}>
          {ins.headline && (
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.5 }}
            >
              {ins.headline}
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 1.5,
            }}
          >
            {ins.strengths?.length > 0 && (
              <Box>
                <Typography sx={sectionSx}>
                  {isRu ? 'Сильные стороны' : 'Strengths'}
                </Typography>
                {ins.strengths.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'flex-start',
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 16, color: 'success.main', mt: '3px' }}
                    />
                    <Typography variant="body2" sx={{ color: 'success.dark' }}>
                      {s}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {ins.nextSteps?.length > 0 && (
              <Box>
                <Typography sx={sectionSx}>
                  {isRu ? 'Следующие шаги на уроках' : 'Next steps in lessons'}
                </Typography>
                {ins.nextSteps.map((s, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>
                    {i + 1}. {s}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
          {ins.improve?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={sectionSx}>
                {isRu ? 'Что улучшить' : 'What to improve'}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {ins.improve.map((it, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      // tinted, readable in light and dark mode
                      bgcolor: (th) => alpha(th.palette.warning.main, 0.08),
                      border: '1px solid',
                      borderColor: (th) => alpha(th.palette.warning.main, 0.35),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {it.area}
                    </Typography>
                    {it.why && (
                      <Typography variant="body2" color="text.secondary">
                        {it.why}
                      </Typography>
                    )}
                    {it.howToPractice && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <b>{isRu ? 'Как тренировать:' : 'How to practise:'}</b>{' '}
                        {it.howToPractice}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            {ins.parentTip && (
              <Box>
                <Typography sx={sectionSx}>
                  {isRu ? 'Совет родителям' : 'Tip for parents'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                  }}
                >
                  {ins.parentTip}
                </Typography>
              </Box>
            )}
            {ins.teacherComment && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography sx={sectionSx}>
                    {isRu ? 'Комментарий преподавателя' : 'Teacher comment'}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon sx={{ fontSize: 14 }} />}
                    onClick={() => copyComment(ins.teacherComment)}
                    sx={{ mb: 0.5, py: 0, fontSize: '0.75rem' }}
                  >
                    {copied
                      ? isRu
                        ? 'Скопировано'
                        : 'Copied'
                      : isRu
                        ? 'Копировать'
                        : 'Copy'}
                  </Button>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {ins.teacherComment}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

const normName = (s) =>
  String(s ?? '')
    .trim()
    .toLowerCase();

function pluralRu(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

function assessmentTypeLabel(type, language) {
  const labels =
    language === 'en'
      ? { initial: 'Initial', 'mid-term': 'Mid-term', final: 'Final' }
      : {
          initial: 'Начальная',
          'mid-term': 'Промежуточная',
          final: 'Итоговая',
        };
  return labels[type || 'initial'] || type;
}

const pctColor = (pct) =>
  pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';

// Same 0 · 1 · 2 order and labels as speed mode
const SCORE_OPTIONS = [
  { value: 0, ru: 'Не знает', en: 'Not yet', color: 'error' },
  { value: 1, ru: 'Частично', en: 'Partly', color: 'warning' },
  { value: 2, ru: 'Знает', en: 'Knows', color: 'success' },
];

// Compact segmented score control for one item
function ScoreSegment({ value, onChange, disabled, language }) {
  return (
    <Box
      role="radiogroup"
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {SCORE_OPTIONS.map((o, i) => {
        const active = value === o.value;
        const label = language === 'en' ? o.en : o.ru;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={active}
            title={`${label} (${o.value})`}
            disabled={disabled}
            tabIndex={-1}
            onClick={() => onChange(o.value)}
            sx={(theme) => ({
              minWidth: { xs: 62, sm: 74 },
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: active ? 700 : 500,
              lineHeight: 1.4,
              color: active
                ? theme.palette[o.color].contrastText
                : theme.palette.text.secondary,
              bgcolor: active ? theme.palette[o.color].main : 'transparent',
              borderLeft: i > 0 ? `1px solid ${theme.palette.divider}` : 'none',
              transition: 'background-color 0.12s',
              '&:hover': {
                bgcolor: active
                  ? theme.palette[o.color].dark
                  : alpha(theme.palette[o.color].main, 0.12),
              },
            })}
          >
            {label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

const templateIdOf = (p) =>
  String(
    (typeof p?.templateId === 'object' ? p?.templateId?._id : p?.templateId) ??
      ''
  );

// --- Main Page ---
export default function StudentProfilePage({ userRole }) {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const { user, language } = useStore();
  const t = translations[language];
  const [tabValue, setTabValue] = useState(0);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'pending' | 'saving' | 'saved' | 'error'
  const [assessmentType, setAssessmentType] = useState('initial');
  const [profileName, setProfileName] = useState('');
  const [student, setStudent] = useState(null);
  const [pdfPanelOpen, setPdfPanelOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);
  // Homework dialog state
  const [hwTab, setHwTab] = useState(0);
  const [hwHistory, setHwHistory] = useState([]);
  const [hwSelected, setHwSelected] = useState(null); // AIContent doc
  const [hwText, setHwText] = useState('');
  const [hwStructured, setHwStructured] = useState(null); // parsed JSON from AI
  const [hwEditMode, setHwEditMode] = useState(false); // true = show raw textarea

  function tryParseStructured(text) {
    if (!text) return null;
    try {
      const p = JSON.parse(text.trim());
      if (p && p.themes) return p;
    } catch {}
    const stripped = text
      .replace(/```json\s*/gi, '')
      .replace(/```/g, '')
      .trim();
    try {
      const p = JSON.parse(stripped);
      if (p && p.themes) return p;
    } catch {}
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end > start) {
      try {
        const p = JSON.parse(text.slice(start, end + 1));
        if (p && p.themes) return p;
      } catch {}
    }
    return null;
  }

  // Structured AI JSON is only valid while the text is the AI original —
  // once the teacher edited it, the plain `content` is the source of truth.
  function structuredFor(item) {
    if (!item) return null;
    const edited =
      item.edited ||
      (item.originalContent != null && item.content !== item.originalContent);
    if (edited) return tryParseStructured(item.content);
    return item.structuredContent || tryParseStructured(item.content) || null;
  }

  function applyHwItem(item) {
    setHwHistory((prev) => prev.map((h) => (h._id === item._id ? item : h)));
    setHwSelected(item);
    setHwText(item.content || '');
    setHwStructured(structuredFor(item));
    setHwEditMode(false);
  }
  const [hwGenerating, setHwGenerating] = useState(false);
  const [hwSaving, setHwSaving] = useState(false);
  const [hwPdfLoading, setHwPdfLoading] = useState(false);
  const [hwTplText, setHwTplText] = useState(''); // template tab text
  const [hwTplSaving, setHwTplSaving] = useState(false);
  // Page-wide snackbar (homework dialog + profile messages)
  const [hwSnack, setHwSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSnack = (message, severity = 'success') =>
    setHwSnack({ open: true, message, severity });
  const hwSectionSx = {
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#57534e',
    mb: 0.75,
  };

  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [openSpeedMode, setOpenSpeedMode] = useState(false);
  const [speedThemes, setSpeedThemes] = useState(null);
  // Increment this after any profile save/create/reassess to trigger Timeline & Impact re-fetch
  const [profilesVersion, setProfilesVersion] = useState(0);

  // Theme selection for new assessment
  const [themeSelectionOpen, setThemeSelectionOpen] = useState(false);
  // «+ Юниты»: grow an existing assessment (Unit 1 → Unit 1 + Unit 2)
  const [addUnitsOpen, setAddUnitsOpen] = useState(false);
  const [addUnitsIds, setAddUnitsIds] = useState([]);
  const [addUnitsBusy, setAddUnitsBusy] = useState(false);
  // Hidden («скрытые») assessments: kept, but out of stats/reports/AI
  const [showHidden, setShowHidden] = useState(false);
  const [hideTarget, setHideTarget] = useState(null);
  const [hideReason, setHideReason] = useState('');
  const [hideBusy, setHideBusy] = useState(false);
  const [selectedThemeIds, setSelectedThemeIds] = useState([]);

  // Template sync
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null); // { orphaned, missing, renamedThemes, renamedItems }
  const [syncRemoving, setSyncRemoving] = useState([]); // theme names to remove
  const [syncAdding, setSyncAdding] = useState(false);
  const [syncUpdateThemeNames, setSyncUpdateThemeNames] = useState(true);
  const [syncUpdateItemTexts, setSyncUpdateItemTexts] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);

  // Theme filter for viewing assessment
  const [activeThemeFilters, setActiveThemeFilters] = useState([]);

  // Focus mode: hide mastered items
  const [focusMode, setFocusMode] = useState(false);

  // Scoring view UI (reset when another profile is opened)
  const [collapsedThemes, setCollapsedThemes] = useState([]); // theme indexes
  const [openComments, setOpenComments] = useState([]); // `${tIdx}-${iIdx}`
  const [sessionScored, setSessionScored] = useState(() => new Set());
  const scoringRef = useRef(null);

  // Session notes
  const [sessionNotes, setSessionNotes] = useState('');

  // Version history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);

  // Reassess confirmation (profile to reassess, or null)
  const [reassessTarget, setReassessTarget] = useState(null);
  // «Поделиться»: a public link to the selected assessment (+ its videos)
  const [shareOpen, setShareOpen] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState('');

  const isTeacher = userRole === 'teacher' || userRole === 'admin';
  const isParent = userRole === 'parent';
  // Older versions of a reassessed chain are history — view only
  const viewingOldVersion = selectedProfile?.isLatest === false;
  const canEdit = isTeacher && !viewingOldVersion;

  // Auto-save refs — always hold latest values so debounced fn captures them
  const autoSaveTimerRef = useRef(null);
  const latestProfileRef = useRef(null);
  const latestNotesRef = useRef('');
  // Payload captured when the save was scheduled: { profileId, themes, notes, seq }
  const pendingSaveRef = useRef(null);
  const inFlightSaveRef = useRef(null); // promise of the save being sent
  const saveSeqRef = useRef(0);
  const flushAutoSaveRef = useRef(null);

  const BACKUP_KEY = (pid) => `profile_backup_${pid}`;

  function saveBackupToStorage(profile, notes) {
    if (!profile?._id) return;
    try {
      localStorage.setItem(
        BACKUP_KEY(profile._id),
        JSON.stringify({
          themes: profile.themes,
          totalScore: profile.totalScore,
          notes,
          savedAt: Date.now(),
        })
      );
    } catch (e) {
      /* storage full — ignore */
    }
  }

  // Sends one captured payload. Resolves true on success, false on failure.
  async function doAutoSave({ profileId, themes, notes, seq }) {
    setAutoSaveStatus('saving');
    try {
      const res = await fetch(`/api/profiles/${profileId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ themes, notes }),
      });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(saved.message || `HTTP ${res.status}`);
      // Keep the list entry in sync with what the server stored
      setProfiles((prev) =>
        prev.map((p) =>
          p._id === saved._id
            ? {
                ...p,
                themes: saved.themes,
                notes: saved.notes,
                totalScore: saved.totalScore,
                maxScore: saved.maxScore,
                updatedAt: saved.updatedAt,
              }
            : p
        )
      );
      setSelectedProfile((prev) =>
        prev?._id === saved._id &&
        (prev.totalScore !== saved.totalScore ||
          prev.maxScore !== saved.maxScore)
          ? {
              ...prev,
              totalScore: saved.totalScore,
              maxScore: saved.maxScore,
            }
          : prev
      );
      // Only the newest edit may clear the backup / show "saved"
      if (seq === saveSeqRef.current) {
        try {
          localStorage.removeItem(BACKUP_KEY(profileId));
        } catch (e) {}
        setAutoSaveError('');
        setAutoSaveStatus('saved');
        setTimeout(
          () => setAutoSaveStatus((s) => (s === 'saved' ? null : s)),
          2000
        );
      }
      return true;
    } catch (err) {
      if (seq === saveSeqRef.current) {
        setAutoSaveStatus('error');
        setAutoSaveError(err.message);
        showSnack(
          `${language === 'en' ? 'Save failed' : 'Ошибка сохранения'}: ${err.message}`,
          'error'
        );
      }
      return false;
    }
  }

  // Capture profile id + payload NOW, so a profile switch within the debounce
  // window can't redirect the save to another profile.
  function scheduleAutoSave(profile, notes) {
    if (!profile?._id || !isTeacher || profile.isLatest === false) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    saveSeqRef.current += 1;
    pendingSaveRef.current = {
      profileId: profile._id,
      themes: profile.themes,
      notes,
      seq: saveSeqRef.current,
    };
    setAutoSaveStatus('pending');
    autoSaveTimerRef.current = setTimeout(flushAutoSave, 1200);
  }

  // Send any pending autosave immediately (or wait for the one in flight).
  // Resolves true if nothing failed.
  function flushAutoSave() {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!pending) return inFlightSaveRef.current || Promise.resolve(true);
    const p = doAutoSave(pending).finally(() => {
      if (inFlightSaveRef.current === p) inFlightSaveRef.current = null;
    });
    inFlightSaveRef.current = p;
    return p;
  }

  function retryAutoSave() {
    scheduleAutoSave(latestProfileRef.current, latestNotesRef.current);
    flushAutoSave();
  }

  useEffect(() => {
    flushAutoSaveRef.current = flushAutoSave;
  });
  // Don't lose a pending save when the page unmounts
  useEffect(
    () => () => {
      flushAutoSaveRef.current?.();
    },
    []
  );

  useEffect(() => {
    latestProfileRef.current = selectedProfile;
  }, [selectedProfile]);

  // Detect out-of-sync themes/items whenever profile or templates change
  useEffect(() => {
    if (!selectedProfile || templates.length === 0) {
      setSyncInfo(null);
      return;
    }
    const tplId =
      typeof selectedProfile.templateId === 'object'
        ? selectedProfile.templateId?._id
        : selectedProfile.templateId;
    const template = templates.find(
      (t) => t._id === tplId || t._id === tplId?.toString()
    );
    if (!template) {
      setSyncInfo(null);
      return;
    }

    // Build lookup maps for template themes and items
    const tplThemeById = {};
    const tplThemeByName = {};
    const tplItemById = {};
    for (const tt of template.themes) {
      tplThemeById[tt._id?.toString()] = tt;
      tplThemeByName[tt.name] = tt;
      for (const ti of tt.items || []) {
        tplItemById[ti._id?.toString()] = { ...ti, themeName: tt.name };
      }
    }

    // Themes in profile that no longer exist in template (by id or name)
    const orphaned = selectedProfile.themes.filter(
      (pt) =>
        !tplThemeById[pt.themeId?.toString()] && !tplThemeByName[pt.themeName]
    );

    // In-scope template themes not yet in the profile. A partial assessment
    // (assessedThemeIds) owns only its units — the rest of the template is
    // excluded on purpose (added via «Юниты»), same rule as the server.
    const profileThemeNames = new Set(
      selectedProfile.themes.map((t) => t.themeName)
    );
    const profileThemeIds = new Set(
      selectedProfile.themes.map((t) => t.themeId?.toString()).filter(Boolean)
    );
    const scoped = (selectedProfile.assessedThemeIds || []).map(String);
    const missing = template.themes.filter(
      (tt) =>
        (scoped.length === 0 || scoped.includes(String(tt._id))) &&
        !profileThemeNames.has(tt.name) &&
        !profileThemeIds.has(String(tt._id))
    );

    // Themes whose name changed (matched by themeId)
    const renamedThemes = [];
    for (const pt of selectedProfile.themes) {
      const tplTheme = tplThemeById[pt.themeId?.toString()];
      if (tplTheme && tplTheme.name !== pt.themeName) {
        renamedThemes.push({ oldName: pt.themeName, newName: tplTheme.name });
      }
    }

    // Items whose text changed (matched by itemId)
    const renamedItems = [];
    for (const pt of selectedProfile.themes) {
      for (const pi of pt.items || []) {
        const tplItem = tplItemById[pi.itemId?.toString()];
        if (tplItem && tplItem.text !== pi.itemText) {
          renamedItems.push({
            themeName: pt.themeName,
            oldText: pi.itemText,
            newText: tplItem.text,
          });
        }
      }
    }

    if (
      orphaned.length > 0 ||
      missing.length > 0 ||
      renamedThemes.length > 0 ||
      renamedItems.length > 0
    ) {
      setSyncInfo({ orphaned, missing, renamedThemes, renamedItems });
    } else {
      setSyncInfo(null);
    }
  }, [selectedProfile, templates]);
  useEffect(() => {
    latestNotesRef.current = sessionNotes;
  }, [sessionNotes]);

  useEffect(() => {
    if (userRole === 'teacher' || userRole === 'admin') {
      loadProfiles();
      fetch('/api/templates', { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => setTemplates(Array.isArray(data) ? data : []));
      loadStudent();
    } else if (userRole === 'parent') {
      setProfilesLoading(true);
      fetch(`/api/parents/student/${id}/progress`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => {
          setProfiles(Array.isArray(data.profiles) ? data.profiles : []);
          loadStudent();
        })
        .catch(() =>
          showSnack(
            language === 'en'
              ? 'Could not load assessments'
              : 'Не удалось загрузить оценки',
            'error'
          )
        )
        .finally(() => setProfilesLoading(false));
    }
  }, [id, userRole]);

  // selectId: profile to keep selected after the reload (e.g. a version that
  // was just created). Defaults to the currently selected profile.
  function loadProfiles(selectId) {
    setProfilesLoading(true);
    const currentId = selectId || latestProfileRef.current?._id;
    fetch(`/api/profiles/student/${id}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        return data;
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setProfiles(list);
        if (currentId) {
          const stillThere = list.find((p) => p._id === currentId);
          // Don't overwrite local edits that are still waiting to be saved
          if (
            stillThere &&
            pendingSaveRef.current?.profileId !== stillThere._id
          ) {
            setSelectedProfile(stillThere);
          }
        }
      })
      .catch((err) =>
        showSnack(
          `${language === 'en' ? 'Could not load profiles' : 'Не удалось загрузить оценки'}: ${err.message}`,
          'error'
        )
      )
      .finally(() => setProfilesLoading(false));
  }

  function loadStudent() {
    fetch(`/api/students/${id}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setStudent(data))
      .catch(() => {});
  }

  function applySelectedProfile(profile, notes) {
    const n = notes !== undefined ? notes : profile?.notes || '';
    latestProfileRef.current = profile;
    latestNotesRef.current = n;
    setSelectedProfile(profile);
    setSessionNotes(n);
    setActiveThemeFilters([]);
    setFocusMode(false);
    setCollapsedThemes([]);
    setOpenComments([]);
    setSessionScored(new Set());
  }

  function handleProfileSelect(e) {
    const profileId = e.target.value;
    // Send the previous profile's pending edits now (payload carries its id)
    flushAutoSave();
    if (!profileId) {
      applySelectedProfile(null);
      return;
    }
    const chosenProfile = profiles.find((p) => p._id === profileId);
    if (!chosenProfile) return;

    // Check for unsaved backup in localStorage
    try {
      const backup = JSON.parse(localStorage.getItem(BACKUP_KEY(profileId)));
      if (
        backup &&
        backup.savedAt &&
        Array.isArray(backup.themes) &&
        backup.themes.length > 0
      ) {
        const age = Math.round((Date.now() - backup.savedAt) / 60000);
        const restore = window.confirm(
          language === 'en'
            ? `Unsaved changes found (${age} min ago). Restore them?`
            : `Найдены несохраненные изменения (${age} мин назад). Восстановить?`
        );
        if (restore) {
          const { totalScore, maxScore } = recalcScores(backup.themes);
          const restored = {
            ...chosenProfile,
            themes: backup.themes,
            totalScore,
            maxScore,
          };
          const notes =
            typeof backup.notes === 'string'
              ? backup.notes
              : chosenProfile.notes || '';
          applySelectedProfile(restored, notes);
          showSnack(
            language === 'en'
              ? 'Restored from backup — saving…'
              : 'Восстановлено из резервной копии — сохраняем…',
            'info'
          );
          scheduleAutoSave(restored, notes);
          return;
        } else {
          localStorage.removeItem(BACKUP_KEY(profileId));
        }
      }
    } catch (e) {}

    applySelectedProfile(chosenProfile);
  }

  function recalcScores(themes) {
    const totalScore = themes.reduce(
      (sum, t) => sum + (t.items || []).reduce((s, i) => s + (i.score || 0), 0),
      0
    );
    const maxScore = themes.reduce(
      (sum, t) => sum + (t.items?.length || 0) * 2,
      0
    );
    return { totalScore, maxScore };
  }

  // Shared path for every in-place edit of the selected profile:
  // state + ref + localStorage backup + debounced autosave.
  function applyProfileEdit(updated) {
    const { totalScore, maxScore } = recalcScores(updated.themes);
    updated.totalScore = totalScore;
    updated.maxScore = maxScore;
    latestProfileRef.current = updated;
    setSelectedProfile(updated);
    saveBackupToStorage(updated, latestNotesRef.current);
    scheduleAutoSave(updated, latestNotesRef.current);
  }

  // Edits build on the ref (updated synchronously) so fast keyboard scoring
  // never works from a stale render
  const editBase = () => latestProfileRef.current || selectedProfile;

  function markScored(keys) {
    setSessionScored((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
  }

  function handleScoreChange(tIdx, iIdx, val) {
    if (!canEdit) return;
    const updated = { ...editBase() };
    updated.themes = updated.themes.map((t, ti) =>
      ti !== tIdx
        ? t
        : {
            ...t,
            items: t.items.map((it, ii) =>
              ii !== iIdx ? it : { ...it, score: Number(val) }
            ),
          }
    );
    applyProfileEdit(updated);
    markScored([`${tIdx}-${iIdx}`]);
  }

  function handleMarkThemeMastered(tIdx) {
    if (!canEdit) return;
    const theme = editBase().themes[tIdx];
    if (
      !theme ||
      !window.confirm(
        language === 'en'
          ? `Mark every item in "${theme.themeName}" as mastered (2)?`
          : `Отметить все пункты темы «${theme.themeName}» как «Знает» (2)?`
      )
    )
      return;
    const updated = { ...editBase() };
    updated.themes = updated.themes.map((t, ti) =>
      ti !== tIdx
        ? t
        : { ...t, items: t.items.map((it) => ({ ...it, score: 2 })) }
    );
    applyProfileEdit(updated);
    markScored(theme.items.map((_, ii) => `${tIdx}-${ii}`));
  }

  function handleCommentChange(tIdx, iIdx, val) {
    if (!canEdit) return;
    const updated = { ...editBase() };
    updated.themes = updated.themes.map((t, ti) =>
      ti !== tIdx
        ? t
        : {
            ...t,
            items: t.items.map((it, ii) =>
              ii !== iIdx ? it : { ...it, comment: val }
            ),
          }
    );
    applyProfileEdit(updated);
  }

  async function handleRenameProfile() {
    const pid = selectedProfile._id;
    try {
      const res = await fetch(`/api/profiles/${pid}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileName: newName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setEditingName(false);
      setSelectedProfile((prev) =>
        prev?._id === pid ? { ...prev, profileName: newName } : prev
      );
      loadProfiles();
    } catch (err) {
      showSnack(
        `${language === 'en' ? 'Rename failed' : 'Не удалось переименовать'}: ${err.message}`,
        'error'
      );
    }
  }

  async function exportPDF() {
    await flushAutoSave();
    try {
      const res = await fetch(
        `/api/pdf/profile/${selectedProfile._id}?lang=${language === 'en' ? 'en' : 'ru'}`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.message || `PDF error: ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFileName(
        res,
        `${student?.name || 'report'}_${language === 'en' ? 'report' : 'отчет'}.pdf`
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        language === 'en' ? 'Could not create PDF' : 'Не удалось создать PDF'
      );
    }
  }

  async function generateAssignment() {
    // AI homework is generated from the server copy — send pending edits first
    await flushAutoSave();
    // Build structured theme data
    const themeData = [];
    let totalMastered = 0;
    selectedProfile.themes.forEach((theme) => {
      const needsWork = theme.items.filter((i) => i.score === 0);
      const developing = theme.items.filter((i) => i.score === 1);
      const mastered = theme.items.filter((i) => i.score === 2);
      totalMastered += mastered.length;
      themeData.push({
        name: theme.themeName,
        needsWork,
        developing,
        mastered,
      });
    });

    const parentNames =
      student?.parentIds
        ?.map((p) => p.name || p.username)
        .join(language === 'en' ? ' and ' : ' и ') ||
      (language === 'en' ? 'Parent' : 'Родитель');

    const data = {
      themeData,
      totalMastered,
      parentNames,
      studentName: student?.name,
      teacherName:
        user?.displayName ||
        user?.username ||
        (language === 'en' ? 'Teacher' : 'Преподаватель'),
      profileName: selectedProfile.profileName,
      date: new Date(),
    };
    setAssignmentData(data);
    // Reset homework dialog state
    setHwTab(0);
    setHwSelected(null);
    setHwText('');
    setHwStructured(null);
    setHwEditMode(false);
    setHwHistory([]);
    loadHwHistory(selectedProfile._id);
    loadHwTemplate(data);
    setAssignmentOpen(true);
  }

  function buildAssignmentText(data) {
    const {
      themeData,
      totalMastered,
      parentNames,
      studentName,
      teacherName,
      profileName,
      date,
    } = data;
    const dateStr = date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    let text = `ДОМАШНЕЕ ЗАДАНИЕ\n`;
    text += `Дата: ${dateStr}\n`;
    text += `Ученик: ${studentName}\n`;
    if (profileName) text += `Программа: ${profileName}\n`;
    text += `Преподаватель: ${teacherName}\n`;
    text += `${'─'.repeat(50)}\n\n`;
    text += `Уважаемые ${parentNames}!\n\n`;
    text += `По результатам занятия определены темы для домашней практики.\n`;
    text += `Рекомендуем заниматься 5–10 минут в день.\n\n`;

    themeData.forEach((theme) => {
      text += `📚 ${theme.name.toUpperCase()}\n`;
      if (theme.needsWork.length > 0) {
        text += `  🔴 Новый материал (выучить):\n`;
        theme.needsWork.forEach((item) => {
          const label = item.itemType === 'phrase' ? '[фраза]' : '[слово]';
          text += `    • ${item.itemText}  ${label}\n`;
        });
      }
      if (theme.developing.length > 0) {
        text += `  🟡 Закрепить (повторить):\n`;
        theme.developing.forEach((item) => {
          const label = item.itemType === 'phrase' ? '[фраза]' : '[слово]';
          text += `    • ${item.itemText}  ${label}\n`;
        });
      }
      text += `\n`;
    });

    if (totalMastered > 0) {
      text += `✅ ОТЛИЧНО УСВОЕНО: ${totalMastered} слов и выражений — похвалите ребёнка!\n\n`;
    }

    text += `${'─'.repeat(50)}\n`;
    text += `КАК ЗАНИМАТЬСЯ ДОМА:\n`;
    text += `1. Покажите карточку или назовите слово по-русски — пусть ребёнок скажет по-английски.\n`;
    text += `2. Составьте простое предложение с каждым новым словом.\n`;
    text += `3. Для фраз: проиграйте диалог или ситуацию.\n`;
    text += `4. Повторяйте красным список ежедневно, жёлтый — через день.\n\n`;
    text += `С уважением,\n${teacherName}`;
    return text;
  }

  function downloadAssignment(data) {
    const text = buildAssignmentText(data);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ДЗ_${data.studentName}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function copyAssignment(data) {
    const text = buildAssignmentText(data);
    navigator.clipboard.writeText(text).catch(() => {});
  }

  // ── Homework dialog helpers ────────────────────────────────────────────────
  async function loadHwHistory(profileId) {
    try {
      const res = await fetch(`/api/ai/homework/by-profile/${profileId}`, {
        credentials: 'include',
      });
      if (res.ok) setHwHistory(await res.json());
    } catch {
      /* ignore */
    }
  }

  function buildDefaultTemplate(data) {
    if (!data) return '';
    const { themeData, studentName, teacherName, date } = data;
    const dateStr = date
      ? date.toLocaleDateString(language === 'en' ? 'en-GB' : 'ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
    const allWeak = themeData
      .flatMap((t) => t.needsWork.map((i) => i.itemText))
      .join(', ');
    const allDev = themeData
      .flatMap((t) => t.developing.map((i) => i.itemText))
      .join(', ');
    if (language === 'en') {
      return `Dear parents of ${studentName},

After today's lesson we recommend practising the following at home (5–10 min/day):

${allWeak ? `Learn (new): ${allWeak}\n` : ''}${allDev ? `Review (consolidate): ${allDev}\n` : ''}
How to practise:
• Show the word in English — let the child say it in their language
• For phrases — act out a short dialogue
• Red list: every day  ·  Yellow list: every other day

Warm regards,
${teacherName}`;
    }
    return `Уважаемые родители ${studentName}!

По итогам сегодняшнего занятия рекомендуем поработать дома (5–10 мин в день):

${allWeak ? `Выучить (новое): ${allWeak}\n` : ''}${allDev ? `Закрепить (повторить): ${allDev}\n` : ''}
Как заниматься:
• Назовите слово по-русски — пусть ребёнок скажет по-английски
• Для фраз — разыграйте короткий диалог
• Красный список: каждый день  ·  Жёлтый список: через день

С уважением,
${teacherName}`;
  }

  async function loadHwTemplate(data) {
    try {
      const res = await fetch('/api/ai/templates', { credentials: 'include' });
      if (res.ok) {
        const tpls = await res.json();
        if (tpls.homeworkSheet && tpls.homeworkSheet.trim()) {
          // Fill placeholders from profile data
          const themeNames = data.themeData.map((t) => t.name).join(', ');
          const weakItems = data.themeData
            .flatMap((t) => t.needsWork.map((i) => i.itemText))
            .join(', ');
          const devItems = data.themeData
            .flatMap((t) => t.developing.map((i) => i.itemText))
            .join(', ');
          const masterItems = data.themeData
            .flatMap((t) => t.mastered.map((i) => i.itemText))
            .join(', ');
          const dateStr = data.date
            ? data.date.toLocaleDateString(
                language === 'en' ? 'en-GB' : 'ru-RU',
                { day: 'numeric', month: 'long', year: 'numeric' }
              )
            : '';
          const filled = tpls.homeworkSheet
            .replace(/\{studentName\}/g, data.studentName || '')
            .replace(/\{teacherName\}/g, data.teacherName || '')
            .replace(/\{date\}/g, dateStr)
            .replace(/\{themeNames\}/g, themeNames)
            .replace(/\{weakItems\}/g, weakItems || '—')
            .replace(/\{developingItems\}/g, devItems || '—')
            .replace(/\{masteredItems\}/g, masterItems || '—')
            .replace(
              /\{score\}/g,
              (() => {
                const s = selectedProfile?.totalScore ?? 0;
                const m = selectedProfile?.maxScore ?? 0;
                return m > 0 ? `${Math.round((s / m) * 100)}%` : '—';
              })()
            );
          setHwTplText(filled);
        } else {
          setHwTplText(buildDefaultTemplate(data));
        }
      }
    } catch {
      setHwTplText(buildDefaultTemplate(data));
    }
  }

  async function hwGenerate() {
    if (!selectedProfile) return;
    setHwGenerating(true);
    try {
      const res = await fetch('/api/ai/generate/profile-homework', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId: selectedProfile._id, language }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 400 = nothing to practise (everything mastered) — not a failure
        showSnack(
          data.message ||
            (language === 'en' ? 'Generation failed' : 'Ошибка генерации'),
          res.status === 400 ? 'info' : 'error'
        );
        return;
      }
      setHwHistory((prev) => [data, ...prev]);
      setHwSelected(data);
      setHwText(data.content || '');
      setHwStructured(structuredFor(data));
      setHwEditMode(false);
      showSnack(language === 'en' ? 'Generated!' : 'Сгенерировано!');
    } catch (err) {
      showSnack(err.message, 'error');
    } finally {
      setHwGenerating(false);
    }
  }

  // Unsaved edits in the homework textarea
  const hwDirty = !!hwSelected && hwText !== (hwSelected.content || '');

  // Returns the saved item, or null on failure
  async function hwSaveEdits() {
    if (!hwSelected) return null;
    setHwSaving(true);
    try {
      const res = await fetch(`/api/ai/content/${hwSelected._id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: hwText }),
      });
      const updated = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(updated.message || `HTTP ${res.status}`);
      // Server drops structuredContent once the text differs from the AI
      // original, so the preview/PDF/Telegram all use the edited text.
      applyHwItem(updated);
      showSnack(language === 'en' ? 'Saved' : 'Сохранено');
      return updated;
    } catch (err) {
      showSnack(
        `${language === 'en' ? 'Save failed' : 'Ошибка сохранения'}: ${err.message}`,
        'error'
      );
      return null;
    } finally {
      setHwSaving(false);
    }
  }

  async function hwResetItem() {
    if (!hwSelected) return;
    setHwSaving(true);
    try {
      const res = await fetch(`/api/ai/content/${hwSelected._id}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      const item = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(item.message || `HTTP ${res.status}`);
      applyHwItem(item);
      showSnack(
        language === 'en' ? 'Original restored' : 'Исходный текст восстановлен'
      );
    } catch (err) {
      showSnack(err.message, 'error');
    } finally {
      setHwSaving(false);
    }
  }

  async function hwDeleteItem(id) {
    if (
      !window.confirm(
        language === 'en' ? 'Delete this homework?' : 'Удалить это задание?'
      )
    )
      return;
    try {
      const res = await fetch(`/api/ai/content/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `HTTP ${res.status}`);
      }
    } catch (err) {
      showSnack(err.message, 'error');
      return;
    }
    setHwHistory((prev) => prev.filter((h) => h._id !== id));
    if (hwSelected?._id === id) {
      setHwSelected(null);
      setHwText('');
      setHwStructured(null);
    }
  }

  async function hwSaveTemplate(data) {
    setHwTplSaving(true);
    try {
      const res = await fetch('/api/ai/save/profile-homework', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileId: selectedProfile._id,
          studentId: student?._id,
          studentName: data.studentName,
          content: hwTplText,
          source: 'template',
          title:
            language === 'en'
              ? `HW template — ${data.studentName} — ${new Date().toLocaleDateString('en-GB')}`
              : `Дз (шаблон) — ${data.studentName} — ${new Date().toLocaleDateString('ru-RU')}`,
        }),
      });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(saved.message || `HTTP ${res.status}`);
      setHwHistory((prev) => [saved, ...prev]);
      showSnack(language === 'en' ? 'Saved to history' : 'Сохранено в историю');
    } catch (err) {
      showSnack(err.message, 'error');
    } finally {
      setHwTplSaving(false);
    }
  }

  async function hwSaveDefaultTemplate() {
    setHwTplSaving(true);
    try {
      const res = await fetch('/api/ai/templates/homework-sheet', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ homeworkSheet: hwTplText }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `HTTP ${res.status}`);
      }
      showSnack(language === 'en' ? 'Template saved!' : 'Шаблон сохранён!');
    } catch (err) {
      showSnack(err.message, 'error');
    } finally {
      setHwTplSaving(false);
    }
  }

  async function hwDownloadPDF(contentText, data, structured) {
    setHwPdfLoading(true);
    try {
      const dateStr = data?.date
        ? data.date.toLocaleDateString(language === 'en' ? 'en-GB' : 'ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
      const res = await fetch('/api/pdf/homework-sheet', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: contentText,
          studentName: data?.studentName || student?.name,
          teacherName: data?.teacherName || user?.username,
          date: dateStr,
          profileName: data?.profileName || selectedProfile?.profileName,
          language,
          themeData: data?.themeData || [],
          aiStructured: structured || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `PDF error: ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${language === 'en' ? 'HW' : 'Дз'}_${data?.studentName || student?.name || 'homework'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setHwSnack({ open: true, message: err.message, severity: 'error' });
    } finally {
      setHwPdfLoading(false);
    }
  }

  async function hwSendTelegram(item) {
    // Telegram sends the server copy — persist unsaved edits first
    let target = item;
    if (hwDirty) {
      target = await hwSaveEdits();
      if (!target) return;
    }
    try {
      const res = await fetch(`/api/ai/content/${target._id}/send`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.message || 'Telegram error');
      showSnack(
        language === 'en' ? 'Sent to Telegram!' : 'Отправлено в Telegram!'
      );
      setHwHistory((prev) => prev.map((h) => (h._id === d._id ? d : h)));
      setHwSelected(d);
    } catch (err) {
      showSnack(err.message, 'error');
    }
  }

  // --- Template selection for theme picker ---
  function handleTemplateSelect(tId) {
    setTemplateId(tId);
    const tmpl = templates.find((t) => t._id === tId);
    setSelectedTemplate(tmpl);
    if (tmpl) {
      setSelectedThemeIds(tmpl.themes.map((t) => t._id));
    }
  }

  const createFailedMsg =
    language === 'en' ? 'Profile creation failed' : 'Не удалось создать оценку';

  function onProfileCreated(newProfile) {
    setProfiles((prev) => [newProfile, ...prev]);
    applySelectedProfile(newProfile, '');
    setTemplateId('');
    setAssessmentType('initial');
    setProfileName('');
    setSelectedThemeIds([]);
    setThemeSelectionOpen(false);
    setError('');
    setProfilesVersion((v) => v + 1);
    // Reload profiles from server to ensure timeline & impact have fresh data
    loadProfiles(newProfile._id);
  }

  async function createProfile(e) {
    e.preventDefault();
    setError('');
    if (!templateId) {
      setError(language === 'en' ? 'Select a template' : 'Выберите шаблон');
      return;
    }

    // If template has themes, show theme picker
    if (
      selectedTemplate &&
      selectedTemplate.themes.length > 1 &&
      !themeSelectionOpen
    ) {
      setThemeSelectionOpen(true);
      return;
    }

    setLoading(true);
    try {
      await flushAutoSave();
      const res = await fetch(`/api/profiles/student/${id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId,
          assessmentType,
          profileName: profileName || undefined,
          assessedThemeIds:
            selectedThemeIds.length > 0 ? selectedThemeIds : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onProfileCreated(data);
      } else {
        setError(data.message || createFailedMsg);
      }
    } catch (err) {
      setError(language === 'en' ? 'Network error' : 'Ошибка сети');
    } finally {
      setLoading(false);
    }
  }

  async function confirmThemeSelection() {
    setThemeSelectionOpen(false);
    setLoading(true);
    try {
      await flushAutoSave();
      const res = await fetch(`/api/profiles/student/${id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId,
          assessmentType,
          profileName: profileName || undefined,
          assessedThemeIds: selectedThemeIds,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || createFailedMsg);
      onProfileCreated(data);
    } catch (err) {
      setError(err.message || createFailedMsg);
    } finally {
      setLoading(false);
    }
  }

  async function handleReassess(profile) {
    setReassessTarget(null);
    setLoading(true);
    try {
      // The new version is pre-filled from the server copy — save edits first
      if (!(await flushAutoSave())) {
        showSnack(
          language === 'en'
            ? 'Could not save current scores — reassessment cancelled'
            : 'Не удалось сохранить текущие оценки — переоценка отменена',
          'error'
        );
        return;
      }
      const res = await fetch(`/api/profiles/student/${id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: templateIdOf(profile),
          assessmentType:
            profile.assessmentType === 'initial' ? 'mid-term' : 'final',
          baseProfileId: profile._id,
        }),
      });
      const newProfile = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(newProfile.message || `HTTP ${res.status}`);
      // Server may have used a newer version of the chain as the base
      setProfiles((prev) => [
        newProfile,
        ...prev.filter(
          (p) =>
            p._id !== profile._id &&
            p._id !== String(newProfile.previousAssessmentId) &&
            p._id !== newProfile._id
        ),
      ]);
      applySelectedProfile(newProfile);
      setProfilesVersion((v) => v + 1);
      loadProfiles(newProfile._id);
      showSnack(
        language === 'en'
          ? `New version v${newProfile.version} created`
          : `Создана новая версия v${newProfile.version}`
      );
    } catch (err) {
      showSnack(
        `${language === 'en' ? 'Reassessment failed' : 'Не удалось переоценить'}: ${err.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }

  async function loadVersionHistory(profileId) {
    try {
      const res = await fetch(`/api/profiles/${profileId}/history`, {
        credentials: 'include',
      });
      const history = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(history.message || `HTTP ${res.status}`);
      setVersionHistory(Array.isArray(history) ? history : []);
      setHistoryOpen(true);
    } catch (err) {
      showSnack(err.message, 'error');
    }
  }

  async function handleProfileDelete(profileId) {
    if (
      !window.confirm(
        language === 'en'
          ? 'Delete this profile? This cannot be undone.'
          : 'Удалить этот профиль? Это действие необратимо.'
      )
    )
      return;
    // Drop a pending autosave for the profile being deleted
    if (pendingSaveRef.current?.profileId === profileId) {
      clearTimeout(autoSaveTimerRef.current);
      pendingSaveRef.current = null;
      setAutoSaveStatus(null);
    }
    try {
      const res = await fetch(`/api/profiles/${profileId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `HTTP ${res.status}`);
      }
      try {
        localStorage.removeItem(BACKUP_KEY(profileId));
      } catch (e) {}
      setProfiles((prev) => prev.filter((p) => p._id !== profileId));
      if (selectedProfile?._id === profileId) applySelectedProfile(null);
    } catch (err) {
      showSnack(err.message, 'error');
    }
  }

  // Speed mode on the selected profile — flush first so the flashcards
  // start from (and save over) the latest scores
  async function startSpeedMode() {
    await flushAutoSave();
    setSpeedThemes(null);
    setOpenSpeedMode(true);
  }

  // From the comparison tab: weak themes of a (possibly old) assessment.
  // Re-assess them on the LATEST profile of that template.
  async function handleReassessWeakThemes({
    profileId,
    templateId,
    themeNames,
  }) {
    await flushAutoSave();
    const latest = (list) =>
      list
        .filter(
          (p) => p.isLatest !== false && templateIdOf(p) === String(templateId)
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    let target =
      profiles.find((p) => p._id === profileId && p.isLatest !== false) ||
      latest(profiles);
    if (target && latestProfileRef.current?._id === target._id) {
      target = latestProfileRef.current; // keeps unsaved local edits
    }
    if (!target) {
      showSnack(
        language === 'en'
          ? 'No current assessment found for this template'
          : 'Не найдена актуальная оценка по этому шаблону',
        'warning'
      );
      return;
    }
    const wanted = new Set((themeNames || []).map(normName));
    const names = (target.themes || [])
      .filter((t) => wanted.has(normName(t.themeName)))
      .map((t) => t.themeName);
    if (names.length === 0) {
      showSnack(
        language === 'en'
          ? 'These themes are not in the current assessment'
          : 'Этих тем нет в актуальной оценке',
        'warning'
      );
      return;
    }
    if (latestProfileRef.current?._id !== target._id) {
      applySelectedProfile(target);
    }
    setTabValue(0);
    setSpeedThemes(names);
    setOpenSpeedMode(true);
  }

  async function handleSync() {
    setSyncLoading(true);
    try {
      // Sync rewrites themes on the server — don't let a stale autosave undo it
      await flushAutoSave();
      const res = await fetch(`/api/profiles/${selectedProfile._id}/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          removeThemeNames: syncRemoving,
          addMissingThemes: syncAdding,
          updateThemeNames: syncUpdateThemeNames,
          updateItemTexts: syncUpdateItemTexts,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSelectedProfile(updated);
        setProfiles((prev) =>
          prev.map((p) => (p._id === updated._id ? updated : p))
        );
        setSyncDialogOpen(false);
        setSyncRemoving([]);
        setSyncAdding(false);
        setSyncUpdateThemeNames(true);
        setSyncUpdateItemTexts(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(
          language === 'en'
            ? `Sync failed: ${err.message || 'server error'}`
            : `Ошибка синхронизации: ${err.message || 'ошибка сервера'}`
        );
      }
    } catch (err) {
      showSnack(err.message, 'error');
    } finally {
      setSyncLoading(false);
    }
  }

  // Units of the profile's template that are not in the profile yet
  function missingUnitsOf(profile) {
    if (!profile) return [];
    const tpl = templates.find(
      (tp) => String(tp._id) === templateIdOf(profile)
    );
    if (!tpl) return [];
    const have = profile.themes || [];
    return (tpl.themes || []).filter(
      (tt) =>
        !have.some(
          (pt) =>
            String(pt.themeId) === String(tt._id) || pt.themeName === tt.name
        )
    );
  }

  // Add units to an existing (latest) assessment instead of creating a new one
  async function addUnitsToProfile(profile, themeIds, assessAfter) {
    if (!profile || !themeIds.length) return false;
    setAddUnitsBusy(true);
    try {
      // The server appends themes — a stale autosave must not drop them again
      if (!(await flushAutoSave())) {
        throw new Error(
          language === 'en'
            ? 'Could not save current scores — try again'
            : 'Не удалось сохранить текущие оценки — попробуйте ещё раз'
        );
      }
      const res = await fetch(`/api/profiles/${profile._id}/themes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ themeIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      const updated = data.profile;
      setProfiles((prev) =>
        prev.map((p) => (p._id === updated._id ? updated : p))
      );
      applySelectedProfile(
        updated,
        latestProfileRef.current?._id === updated._id
          ? latestNotesRef.current
          : undefined
      );
      setProfilesVersion((v) => v + 1);
      const names = (data.added || []).map((a) => a.name);
      showSnack(
        language === 'en'
          ? `Added to the assessment: ${names.join(', ')}`
          : `Добавлено в оценку: ${names.join(', ')}`
      );
      if (assessAfter && names.length) {
        setTabValue(0);
        setSpeedThemes(names);
        setOpenSpeedMode(true);
      }
      return true;
    } catch (err) {
      showSnack(err.message, 'error');
      return false;
    } finally {
      setAddUnitsBusy(false);
    }
  }

  const hiddenCount = profiles.filter((p) => p.excluded).length;
  const listedProfiles = showHidden
    ? profiles
    : profiles.filter((p) => !p.excluded);

  // Hide / unhide an assessment (the server applies it to all its versions)
  async function setProfileExcluded(profile, excluded, reason) {
    if (!profile) return false;
    setHideBusy(true);
    try {
      const res = await fetch(`/api/profiles/${profile._id}/exclude`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excluded, reason: reason || '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      const flags = {
        excluded: data.profile.excluded,
        excludedAt: data.profile.excludedAt,
        excludedReason: data.profile.excludedReason,
      };
      // Merge only the flags — keeps unsaved local scores intact
      setProfiles((prev) =>
        prev.map((p) => (p._id === profile._id ? { ...p, ...flags } : p))
      );
      setSelectedProfile((prev) =>
        prev && prev._id === profile._id ? { ...prev, ...flags } : prev
      );
      if (latestProfileRef.current?._id === profile._id) {
        latestProfileRef.current = { ...latestProfileRef.current, ...flags };
      }
      setProfilesVersion((v) => v + 1);
      showSnack(
        excluded
          ? language === 'en'
            ? 'Assessment hidden — it no longer counts anywhere'
            : 'Оценка скрыта — больше нигде не учитывается'
          : language === 'en'
            ? 'Assessment restored'
            : 'Оценка снова учитывается'
      );
      return true;
    } catch (err) {
      showSnack(err.message, 'error');
      return false;
    } finally {
      setHideBusy(false);
    }
  }

  function getProfileDisplay(profile) {
    const templateName =
      typeof profile.templateId === 'object' && profile.templateId
        ? profile.templateId.name
        : language === 'en'
          ? '(template)'
          : '(шаблон)';
    const created =
      profile.profileName || new Date(profile.createdAt).toLocaleDateString();
    const version = profile.version > 1 ? ` (v${profile.version})` : '';
    return `${templateName} — ${created}${version}`;
  }

  // Filter themes for display
  function getVisibleThemes() {
    if (!selectedProfile) return [];
    let themes = selectedProfile.themes;
    if (activeThemeFilters.length > 0) {
      themes = themes.filter((t) => activeThemeFilters.includes(t.themeName));
    }
    return themes;
  }

  // Get theme score
  function getThemeScore(theme) {
    const max = theme.items.length * 2;
    const score = theme.items.reduce((s, item) => s + item.score, 0);
    return { score, max, pct: max > 0 ? Math.round((score / max) * 100) : 0 };
  }

  // Filter items in focus mode
  function getVisibleItems(theme) {
    if (focusMode) {
      return theme.items.filter((item) => item.score < 2);
    }
    return theme.items;
  }

  // Numbers for the sticky scoring header. "Assessed" = scored in this
  // session or already above 0.
  function getScoringStats() {
    const { totalScore, maxScore } = recalcScores(selectedProfile.themes);
    let items = 0;
    let assessed = 0;
    selectedProfile.themes.forEach((t, ti) =>
      (t.items || []).forEach((it, ii) => {
        items += 1;
        if (it.score > 0 || sessionScored.has(`${ti}-${ii}`)) assessed += 1;
      })
    );
    const pct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    return { totalScore, maxScore, pct, items, assessed };
  }

  const toggleComment = (key) =>
    setOpenComments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  const toggleThemeCollapsed = (tIdx) =>
    setCollapsedThemes((prev) =>
      prev.includes(tIdx) ? prev.filter((i) => i !== tIdx) : [...prev, tIdx]
    );

  // Row keyboard: 0/1/2 set the score, ↑/↓ move between rows.
  // Typing in the comment field is never intercepted.
  function handleScoreRowKey(e, tIdx, iIdx) {
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable)
      return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const rows = Array.from(
      scoringRef.current?.querySelectorAll('[data-score-row]') || []
    );
    const i = rows.indexOf(e.currentTarget);
    const focusRow = (row) => {
      if (!row) return;
      row.focus({ preventScroll: true });
      row.scrollIntoView?.({ block: 'nearest' });
    };
    if (e.key === '0' || e.key === '1' || e.key === '2') {
      e.preventDefault();
      const val = Number(e.key);
      // In focus mode a mastered row disappears — keep the flow going
      const next = focusMode && val === 2 ? rows[i + 1] || rows[i - 1] : null;
      handleScoreChange(tIdx, iIdx, val);
      if (next) setTimeout(() => focusRow(next), 0);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusRow(rows[i + (e.key === 'ArrowDown' ? 1 : -1)]);
    }
  }

  function renderScoreRow(theme, tIdx, item) {
    const iIdx = theme.items.indexOf(item);
    const key = `${tIdx}-${iIdx}`;
    const commentOpen = canEdit && openComments.includes(key);
    return (
      <Box
        key={iIdx}
        data-score-row=""
        tabIndex={canEdit ? 0 : undefined}
        onKeyDown={
          canEdit ? (e) => handleScoreRowKey(e, tIdx, iIdx) : undefined
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 0.5,
          px: { xs: 1, sm: 1.5 },
          py: 0.5,
          minHeight: 40,
          borderTop: '1px solid',
          borderColor: 'divider',
          outline: 'none',
          scrollMarginTop: { xs: 170, sm: 140 }, // AppBar + sticky header
          scrollMarginBottom: { xs: 80, md: 16 },
          '&:focus-within': { bgcolor: 'action.hover' },
          '&:focus-visible': {
            boxShadow: (th) => `inset 3px 0 0 ${th.palette.primary.main}`,
          },
        }}
      >
        <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              fontStyle: item.itemType === 'phrase' ? 'italic' : 'normal',
              wordBreak: 'break-word',
            }}
          >
            {item.itemText}
          </Typography>
          {item.meaning && (
            <Typography
              component="span"
              sx={{ ml: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}
            >
              {item.meaning}
            </Typography>
          )}
          {!canEdit && item.comment && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {item.comment}
            </Typography>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}
        >
          <ScoreSegment
            value={item.score}
            disabled={!canEdit}
            language={language}
            onChange={(v) => handleScoreChange(tIdx, iIdx, v)}
          />
          {canEdit && (
            <Tooltip
              title={
                item.comment || (language === 'en' ? 'Comment' : 'Комментарий')
              }
            >
              <IconButton
                size="small"
                aria-label={language === 'en' ? 'Comment' : 'Комментарий'}
                aria-expanded={commentOpen}
                onClick={() => toggleComment(key)}
                sx={{
                  color:
                    item.comment || commentOpen
                      ? 'primary.main'
                      : 'text.disabled',
                }}
              >
                <Badge variant="dot" color="primary" invisible={!item.comment}>
                  <CommentIcon sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {commentOpen && (
          <Box sx={{ flexBasis: '100%', pb: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={3}
              autoFocus
              placeholder={
                language === 'en'
                  ? 'Comment (Esc to close)'
                  : 'Комментарий (Esc — закрыть)'
              }
              value={item.comment || ''}
              onChange={(e) => handleCommentChange(tIdx, iIdx, e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                const row = e.currentTarget.closest('[data-score-row]');
                toggleComment(key);
                row?.focus();
              }}
              InputProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Layout title={isParent ? t.viewProgress : t.studentProfile}>
      <Container maxWidth="lg">
        {isParent && (
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}
            >
              {language === 'en'
                ? `${student?.name || ''}'s Journey`
                : `Путь: ${student?.name || ''}`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {language === 'en'
                ? 'Review assessments, celebrate achievements, and track growth.'
                : 'Просматривайте оценки и следите за ростом.'}
            </Typography>
          </Box>
        )}

        {/* overflow visible: the scoring header is position: sticky */}
        <Card
          sx={{
            p: { xs: 2, sm: 4 },
            boxShadow: 3,
            borderRadius: 4,
            overflow: 'visible',
          }}
        >
          {!isParent && (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              {student && (
                <Box sx={{ mb: 1.5 }}>
                  <PersonPhotos
                    kind="student"
                    id={id}
                    name={student.name}
                    canEdit={isTeacher}
                    size={104}
                  />
                </Box>
              )}
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.125rem' },
                }}
              >
                {t.studentProfile}: {student?.name || ''}
              </Typography>
              {student?.parentIds?.length > 0 && (
                <Typography variant="subtitle1" color="text.secondary">
                  {language === 'en' ? 'Parents' : 'Родители'}:{' '}
                  <b>
                    {student.parentIds
                      .map((p) => p.name || p.username)
                      .join(', ')}
                  </b>
                </Typography>
              )}
            </Box>
          )}

          {/* «Память»: what every report, homework and the assistant keep in mind */}
          {isTeacher && (
            <Paper
              variant="outlined"
              sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3 }}
            >
              <MemoryPanel
                studentId={id}
                en={language === 'en'}
                title={
                  language === 'en'
                    ? 'Memory about the child'
                    : 'Память о ребёнке'
                }
              />
            </Paper>
          )}

          {/* Smart Insights */}
          {isTeacher && <SmartInsights studentId={id} />}

          {/* AI "where to improve" */}
          {isTeacher && <AiInsightsCard studentId={id} />}

          {/* Theme Growth Chart */}
          {isTeacher && profiles.length >= 2 && (
            <ThemeGrowthChart studentId={id} />
          )}

          {/* Assessment videos of this student (all assessments). Recording
              goes into the open assessment when it is the latest version. */}
          {(isTeacher || isParent) && (
            <ProfileVideosSection
              studentId={id}
              profileId={
                canEdit && selectedProfile ? selectedProfile._id : null
              }
              canRecord={canEdit && !!selectedProfile?._id}
            />
          )}

          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<AssessmentIcon />} label={t.assessments} />
            <Tab icon={<CompareIcon />} label={t.compare} />
            <Tab icon={<CalendarIcon />} label={t.timeline} />
            <Tab icon={<InsightsIcon />} label={t.impact} />
            <Tab icon={<PortfolioIcon />} label={t.portfolio} />
            <Tab icon={<HomeIcon />} label={t.homeActivity} />
            <Tab
              icon={<NoteAltIcon />}
              label={language === 'ru' ? 'Заметки' : 'Notes'}
            />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              {/* Create Profile Form */}
              {isTeacher && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant={createFormOpen ? 'outlined' : 'contained'}
                    startIcon={
                      createFormOpen ? <ExpandLessIcon /> : <AddIcon />
                    }
                    onClick={() => setCreateFormOpen((v) => !v)}
                    sx={{ mb: createFormOpen ? 2 : 0 }}
                  >
                    {createFormOpen
                      ? language === 'ru'
                        ? 'Свернуть'
                        : 'Collapse'
                      : language === 'ru'
                        ? 'Новая оценка'
                        : 'New Assessment'}
                  </Button>
                  <Collapse in={createFormOpen}>
                    <Box component="form" onSubmit={createProfile}>
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <FormControl fullWidth>
                            <InputLabel>{t.template}</InputLabel>
                            <Select
                              value={templateId}
                              label={t.template}
                              onChange={(e) =>
                                handleTemplateSelect(e.target.value)
                              }
                            >
                              <MenuItem value="">
                                <em>
                                  {language === 'en'
                                    ? 'Select Template'
                                    : 'Выбрать шаблон'}
                                </em>
                              </MenuItem>
                              {templates.map((t) => (
                                <MenuItem value={t._id} key={t._id}>
                                  {t.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <FormControl fullWidth>
                            <InputLabel>{t.assessmentType}</InputLabel>
                            <Select
                              value={assessmentType}
                              label={t.assessmentType}
                              onChange={(e) =>
                                setAssessmentType(e.target.value)
                              }
                            >
                              <MenuItem value="initial">
                                {language === 'en'
                                  ? 'Initial Assessment'
                                  : 'Начальная оценка'}
                              </MenuItem>
                              <MenuItem value="mid-term">
                                {language === 'en'
                                  ? 'Mid-term Check'
                                  : 'Промежуточная'}
                              </MenuItem>
                              <MenuItem value="final">
                                {language === 'en'
                                  ? 'Final Assessment'
                                  : 'Итоговая'}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8, md: 3 }}>
                          <TextField
                            fullWidth
                            label={t.profileName}
                            placeholder={
                              language === 'en'
                                ? 'e.g., Mid-term Check'
                                : 'напр. Проверка за сентябрь'
                            }
                            value={profileName}
                            onChange={(e) => setProfileName(e.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            startIcon={
                              loading && <CircularProgress size={20} />
                            }
                            sx={{ borderRadius: 2, py: 1.5 }}
                          >
                            {loading ? '...' : t.create}
                          </Button>
                        </Grid>
                      </Grid>
                      {error && (
                        <Typography color="error" sx={{ mt: 1 }}>
                          {error}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Profile Selector */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {isParent
                  ? language === 'en'
                    ? 'Progress History'
                    : 'История'
                  : language === 'en'
                    ? 'Existing Profiles'
                    : 'Профили'}
              </Typography>
              {profilesLoading ? (
                <Box sx={{ mb: 2 }}>
                  <Skeleton
                    variant="rounded"
                    height={56}
                    sx={{ mb: 1.5, borderRadius: 2 }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={48}
                    sx={{ mb: 1, borderRadius: 2 }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={48}
                    sx={{ mb: 1, borderRadius: 2 }}
                  />
                </Box>
              ) : null}
              <FormControl
                fullWidth
                sx={{ mb: 2, display: profilesLoading ? 'none' : undefined }}
              >
                <InputLabel>
                  {language === 'en' ? 'Select Profile' : 'Выбрать профиль'}
                </InputLabel>
                <Select
                  value={selectedProfile ? selectedProfile._id : ''}
                  label={
                    language === 'en' ? 'Select Profile' : 'Выбрать профиль'
                  }
                  onChange={handleProfileSelect}
                >
                  <MenuItem value="">
                    <em>
                      {language === 'en'
                        ? 'Select a profile'
                        : 'Выберите профиль'}
                    </em>
                  </MenuItem>
                  {listedProfiles.map((profile) => (
                    <MenuItem
                      value={profile._id}
                      key={profile._id}
                      sx={profile.excluded ? { opacity: 0.6 } : undefined}
                    >
                      {getProfileDisplay(profile)}
                      {profile.excluded &&
                        (language === 'en' ? ' · hidden' : ' · скрыта')}
                    </MenuItem>
                  ))}
                  {/* An old version opened from history (or a hidden one)
                      isn't in the list */}
                  {selectedProfile &&
                    !listedProfiles.some(
                      (p) => p._id === selectedProfile._id
                    ) && (
                      <MenuItem value={selectedProfile._id}>
                        {getProfileDisplay(selectedProfile)}
                        {viewingOldVersion &&
                          (language === 'en'
                            ? ' — old version'
                            : ' — старая версия')}
                      </MenuItem>
                    )}
                </Select>
              </FormControl>

              {/* Profile Action Bar */}
              {isTeacher && selectedProfile && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    disabled={viewingOldVersion}
                    onClick={() => {
                      setNewName(selectedProfile.profileName || '');
                      setEditingName(true);
                    }}
                  >
                    {t.rename}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ReplayIcon />}
                    color="secondary"
                    disabled={viewingOldVersion || loading}
                    onClick={() => setReassessTarget(selectedProfile)}
                  >
                    {language === 'en' ? 'Reassess' : 'Переоценить'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<IosShareIcon />}
                    onClick={() => setShareOpen(true)}
                  >
                    {language === 'en' ? 'Share' : 'Поделиться'}
                  </Button>
                  {!viewingOldVersion &&
                    missingUnitsOf(selectedProfile).length > 0 && (
                      <Button
                        size="small"
                        startIcon={<AddCircleIcon />}
                        color="primary"
                        disabled={loading || addUnitsBusy}
                        onClick={() => {
                          setAddUnitsIds([]);
                          setAddUnitsOpen(true);
                        }}
                      >
                        {language === 'en' ? 'Add units' : 'Юниты'} (
                        {missingUnitsOf(selectedProfile).length})
                      </Button>
                    )}
                  <Tooltip
                    title={
                      language === 'en'
                        ? 'Hidden assessments stay in history but do not count in stats, reports, AI or the parents’ view'
                        : 'Скрытая оценка остаётся в истории, но не учитывается в статистике, отчётах, AI и не видна родителям'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={
                          selectedProfile.excluded ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )
                        }
                        disabled={hideBusy}
                        onClick={() => {
                          if (selectedProfile.excluded) {
                            setProfileExcluded(selectedProfile, false);
                          } else {
                            setHideReason('');
                            setHideTarget(selectedProfile);
                          }
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        {selectedProfile.excluded
                          ? language === 'en'
                            ? 'Unhide'
                            : 'Вернуть'
                          : language === 'en'
                            ? 'Hide'
                            : 'Скрыть'}
                      </Button>
                    </span>
                  </Tooltip>
                  {selectedProfile.version > 1 && (
                    <Button
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => loadVersionHistory(selectedProfile._id)}
                    >
                      {language === 'en'
                        ? `History (v${selectedProfile.version})`
                        : `История (v${selectedProfile.version})`}
                    </Button>
                  )}
                  {syncInfo && !viewingOldVersion && (
                    <Tooltip
                      title={
                        language === 'en'
                          ? [
                              syncInfo.orphaned.length > 0 &&
                                `${syncInfo.orphaned.length} removed theme(s)`,
                              syncInfo.missing.length > 0 &&
                                `${syncInfo.missing.length} new theme(s)`,
                              syncInfo.renamedThemes?.length > 0 &&
                                `${syncInfo.renamedThemes.length} renamed theme(s)`,
                              syncInfo.renamedItems?.length > 0 &&
                                `${syncInfo.renamedItems.length} renamed item(s)`,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : [
                              syncInfo.orphaned.length > 0 &&
                                `${syncInfo.orphaned.length} удалённых тем`,
                              syncInfo.missing.length > 0 &&
                                `${syncInfo.missing.length} новых тем`,
                              syncInfo.renamedThemes?.length > 0 &&
                                `${syncInfo.renamedThemes.length} переименованных тем`,
                              syncInfo.renamedItems?.length > 0 &&
                                `${syncInfo.renamedItems.length} переименованных элементов`,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                      }
                    >
                      <Button
                        size="small"
                        startIcon={<WarnIcon />}
                        color="warning"
                        variant="outlined"
                        onClick={() => {
                          setSyncRemoving(
                            syncInfo.orphaned.map((t) => t.themeName)
                          );
                          setSyncAdding(false);
                          setSyncUpdateThemeNames(
                            (syncInfo.renamedThemes?.length ?? 0) > 0
                          );
                          setSyncUpdateItemTexts(
                            (syncInfo.renamedItems?.length ?? 0) > 0
                          );
                          setSyncDialogOpen(true);
                        }}
                      >
                        {language === 'en'
                          ? 'Out of sync'
                          : 'Не синхронизировано'}
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* Add units to this assessment */}
              <Dialog
                open={addUnitsOpen}
                onClose={() => !addUnitsBusy && setAddUnitsOpen(false)}
                maxWidth="xs"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
              >
                <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
                  {language === 'en'
                    ? 'Add units to this assessment'
                    : 'Добавить юниты в эту оценку'}
                </DialogTitle>
                <DialogContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    {language === 'en'
                      ? 'Scores already given stay as they are. New units start as not assessed.'
                      : 'Уже поставленные оценки не меняются. Новые юниты начнутся как «ещё не оценено».'}
                  </Typography>
                  <FormGroup>
                    {missingUnitsOf(selectedProfile).map((theme) => {
                      const n = theme.items?.length || 0;
                      return (
                        <FormControlLabel
                          key={theme._id}
                          control={
                            <Checkbox
                              checked={addUnitsIds.includes(theme._id)}
                              onChange={(e) =>
                                setAddUnitsIds((prev) =>
                                  e.target.checked
                                    ? [...prev, theme._id]
                                    : prev.filter((x) => x !== theme._id)
                                )
                              }
                            />
                          }
                          label={
                            language === 'en'
                              ? `${theme.name} (${n} items)`
                              : `${theme.name} (${n} ${pluralRu(n, 'пункт', 'пункта', 'пунктов')})`
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    onClick={() => setAddUnitsOpen(false)}
                    disabled={addUnitsBusy}
                  >
                    {language === 'en' ? 'Cancel' : 'Отмена'}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!addUnitsIds.length || addUnitsBusy}
                    onClick={async () => {
                      if (
                        await addUnitsToProfile(
                          selectedProfile,
                          addUnitsIds,
                          false
                        )
                      )
                        setAddUnitsOpen(false);
                    }}
                  >
                    {language === 'en' ? 'Add' : 'Добавить'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<BoltIcon />}
                    disabled={!addUnitsIds.length || addUnitsBusy}
                    onClick={async () => {
                      if (
                        await addUnitsToProfile(
                          selectedProfile,
                          addUnitsIds,
                          true
                        )
                      )
                        setAddUnitsOpen(false);
                    }}
                  >
                    {language === 'en'
                      ? 'Add and assess'
                      : 'Добавить и оценить'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Sync Dialog */}
              <Dialog
                open={syncDialogOpen}
                onClose={() => setSyncDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: 3 } }}
              >
                <DialogTitle sx={{ fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SyncIcon color="warning" />
                    {language === 'en'
                      ? 'Sync profile with template'
                      : 'Синхронизировать профиль с шаблоном'}
                  </Box>
                </DialogTitle>
                <DialogContent>
                  {syncInfo?.orphaned.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        {language === 'en'
                          ? 'Themes no longer in template (with student scores):'
                          : 'Темы, удалённые из шаблона (с оценками ученика):'}
                      </Typography>
                      <FormGroup>
                        {syncInfo.orphaned.map((theme) => {
                          const score = theme.items.reduce(
                            (s, i) => s + (i.score || 0),
                            0
                          );
                          const max = theme.items.length * 2;
                          return (
                            <FormControlLabel
                              key={theme.themeName}
                              control={
                                <Checkbox
                                  checked={syncRemoving.includes(
                                    theme.themeName
                                  )}
                                  onChange={(e) =>
                                    setSyncRemoving((prev) =>
                                      e.target.checked
                                        ? [...prev, theme.themeName]
                                        : prev.filter(
                                            (n) => n !== theme.themeName
                                          )
                                    )
                                  }
                                  color="error"
                                />
                              }
                              label={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography variant="body2">
                                    {theme.themeName}
                                  </Typography>
                                  <Chip
                                    label={`${score}/${max}`}
                                    size="small"
                                    color={score > 0 ? 'warning' : 'default'}
                                    variant="outlined"
                                  />
                                  {score > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="warning.main"
                                    >
                                      {language === 'en'
                                        ? '⚠ scores will be lost'
                                        : '⚠ оценки будут удалены'}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Box>
                  )}
                  {syncInfo?.missing.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        {language === 'en'
                          ? 'New themes in template (not yet in this profile):'
                          : 'Новые темы в шаблоне (ещё не в профиле):'}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={syncAdding}
                            onChange={(e) => setSyncAdding(e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            {syncInfo.missing.map((tt) => (
                              <Chip
                                key={tt.name}
                                label={tt.name}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {language === 'en'
                                ? 'Add these themes with blank scores'
                                : 'Добавить эти темы с нулевыми оценками'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}
                  {syncInfo?.renamedThemes?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        {language === 'en'
                          ? 'Themes renamed in template:'
                          : 'Переименованные темы в шаблоне:'}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={syncUpdateThemeNames}
                            onChange={(e) =>
                              setSyncUpdateThemeNames(e.target.checked)
                            }
                            color="info"
                          />
                        }
                        label={
                          <Box>
                            {syncInfo.renamedThemes.map((r) => (
                              <Typography
                                key={r.oldName}
                                variant="caption"
                                sx={{ display: 'block' }}
                                color="text.secondary"
                              >
                                &ldquo;{r.oldName}&rdquo; → &ldquo;
                                {r.newName}&rdquo;
                              </Typography>
                            ))}
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {language === 'en'
                                ? 'Update theme names in this profile (scores kept)'
                                : 'Обновить названия тем в профиле (оценки сохранятся)'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}
                  {syncInfo?.renamedItems?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        {language === 'en'
                          ? 'Items renamed in template:'
                          : 'Переименованные элементы в шаблоне:'}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={syncUpdateItemTexts}
                            onChange={(e) =>
                              setSyncUpdateItemTexts(e.target.checked)
                            }
                            color="info"
                          />
                        }
                        label={
                          <Box>
                            {syncInfo.renamedItems.slice(0, 5).map((r, i) => (
                              <Typography
                                key={i}
                                variant="caption"
                                sx={{ display: 'block' }}
                                color="text.secondary"
                              >
                                &ldquo;{r.oldText}&rdquo; → &ldquo;
                                {r.newText}&rdquo;
                              </Typography>
                            ))}
                            {syncInfo.renamedItems.length > 5 && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                +{syncInfo.renamedItems.length - 5}{' '}
                                {language === 'en' ? 'more' : 'ещё'}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {language === 'en'
                                ? 'Update item text in this profile (scores kept)'
                                : 'Обновить текст элементов в профиле (оценки сохранятся)'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={() => setSyncDialogOpen(false)}>
                    {language === 'en' ? 'Cancel' : 'Отмена'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                      syncLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SyncIcon />
                      )
                    }
                    disabled={
                      syncLoading ||
                      (syncRemoving.length === 0 &&
                        !syncAdding &&
                        !syncUpdateThemeNames &&
                        !syncUpdateItemTexts)
                    }
                    onClick={handleSync}
                  >
                    {language === 'en' ? 'Apply sync' : 'Применить'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Rename Dialog */}
              <Dialog
                open={editingName}
                onClose={() => setEditingName(false)}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle>{t.rename}</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    label={t.profileName}
                    fullWidth
                    variant="standard"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <Button onClick={() => setEditingName(false)}>
                    {language === 'en' ? 'Cancel' : 'Отмена'}
                  </Button>
                  <Button onClick={handleRenameProfile} variant="contained">
                    {language === 'en' ? 'Save' : 'Сохранить'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Profile List */}
              {isTeacher && listedProfiles.length > 0 && (
                <List
                  dense
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    mb: hiddenCount > 0 ? 1 : 4,
                  }}
                >
                  {listedProfiles.map((profile) => (
                    <ListItem
                      key={profile._id}
                      sx={profile.excluded ? { opacity: 0.55 } : undefined}
                      secondaryAction={
                        <Box>
                          <Tooltip
                            title={
                              profile.excluded
                                ? language === 'en'
                                  ? 'Count it again'
                                  : 'Снова учитывать'
                                : language === 'en'
                                  ? 'Hide (keep, but do not count)'
                                  : 'Скрыть (оставить, но не учитывать)'
                            }
                          >
                            <IconButton
                              size="small"
                              disabled={hideBusy}
                              onClick={() => {
                                if (profile.excluded) {
                                  setProfileExcluded(profile, false);
                                } else {
                                  setHideReason('');
                                  setHideTarget(profile);
                                }
                              }}
                            >
                              {profile.excluded ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              language === 'en' ? 'Reassess' : 'Переоценить'
                            }
                          >
                            <IconButton
                              size="small"
                              color="secondary"
                              disabled={loading}
                              onClick={() => setReassessTarget(profile)}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleProfileDelete(profile._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={getProfileDisplay(profile)}
                        secondary={(() => {
                          const { totalScore, maxScore } = recalcScores(
                            profile.themes
                          );
                          const hidden = profile.excluded
                            ? language === 'en'
                              ? ' | hidden'
                              : ' | скрыта'
                            : '';
                          return `${assessmentTypeLabel(profile.assessmentType, language)} | ${language === 'en' ? 'Score' : 'Балл'}: ${totalScore}/${maxScore}${hidden}`;
                        })()}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {isTeacher && hiddenCount > 0 && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={
                    showHidden ? <VisibilityOffIcon /> : <VisibilityIcon />
                  }
                  onClick={() => setShowHidden((v) => !v)}
                  sx={{ mb: 3, color: 'text.secondary' }}
                >
                  {showHidden
                    ? language === 'en'
                      ? 'Hide hidden assessments'
                      : 'Не показывать скрытые оценки'
                    : language === 'en'
                      ? `Show hidden assessments (${hiddenCount})`
                      : `Показать скрытые оценки (${hiddenCount})`}
                </Button>
              )}

              {/* Confirm: hide an assessment */}
              <Dialog
                open={!!hideTarget}
                onClose={() => !hideBusy && setHideTarget(null)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle sx={{ fontWeight: 700 }}>
                  {language === 'en'
                    ? 'Hide this assessment?'
                    : 'Скрыть оценку?'}
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {language === 'en'
                      ? `«${hideTarget?.profileName || ''}» and all its versions stay saved, but stop counting in dashboards, analytics, reports and PDFs, AI homework and the assistant, and parents won’t see it. You can bring it back any time.`
                      : `«${hideTarget?.profileName || ''}» и все её версии останутся сохранены, но перестанут учитываться в главной, аналитике, отчётах и PDF, AI-заданиях и помощнике, и не будут видны родителям. Вернуть можно в любой момент.`}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label={
                      language === 'en'
                        ? 'Reason (optional)'
                        : 'Причина (необязательно)'
                    }
                    placeholder={
                      language === 'en'
                        ? 'e.g. old criteria'
                        : 'например: старые критерии'
                    }
                    value={hideReason}
                    onChange={(e) => setHideReason(e.target.value)}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <Button
                    onClick={() => setHideTarget(null)}
                    disabled={hideBusy}
                  >
                    {language === 'en' ? 'Cancel' : 'Отмена'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<VisibilityOffIcon />}
                    disabled={hideBusy}
                    onClick={async () => {
                      if (
                        await setProfileExcluded(hideTarget, true, hideReason)
                      )
                        setHideTarget(null);
                    }}
                  >
                    {language === 'en' ? 'Hide' : 'Скрыть'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Selected Profile Assessment View */}
              {selectedProfile && (
                <Box sx={{ mt: 4 }}>
                  {selectedProfile.excluded && (
                    <Alert
                      severity="info"
                      icon={<VisibilityOffIcon />}
                      sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
                      action={
                        isTeacher ? (
                          <Button
                            color="inherit"
                            size="small"
                            disabled={hideBusy}
                            onClick={() =>
                              setProfileExcluded(selectedProfile, false)
                            }
                          >
                            {language === 'en' ? 'Unhide' : 'Вернуть'}
                          </Button>
                        ) : null
                      }
                    >
                      {language === 'en'
                        ? 'Hidden assessment — kept, but not counted in stats, reports or AI.'
                        : 'Скрытая оценка — сохранена, но не учитывается в статистике, отчётах и AI.'}
                      {selectedProfile.excludedReason
                        ? ` (${selectedProfile.excludedReason})`
                        : ''}
                    </Alert>
                  )}
                  {viewingOldVersion && (
                    <Alert
                      severity="warning"
                      icon={<HistoryIcon />}
                      sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
                      action={(() => {
                        const latestId = versionHistory.find(
                          (v) => v.isLatest
                        )?._id;
                        const latest = profiles.find((p) => p._id === latestId);
                        return latest ? (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => applySelectedProfile(latest)}
                          >
                            {language === 'en'
                              ? 'Open current'
                              : 'К актуальной версии'}
                          </Button>
                        ) : null;
                      })()}
                    >
                      {language === 'en'
                        ? `This is an old version (v${selectedProfile.version || 1}) — view only`
                        : `Это старая версия (v${selectedProfile.version || 1}) — только просмотр`}
                    </Alert>
                  )}
                  {/* Sticky scoring header */}
                  {(() => {
                    const st = getScoringStats();
                    const tplName =
                      typeof selectedProfile.templateId === 'object'
                        ? selectedProfile.templateId?.name
                        : '';
                    return (
                      <Box
                        sx={{
                          position: 'sticky',
                          top: { xs: 56, sm: 64 },
                          zIndex: 3,
                          mb: 1.5,
                          px: { xs: 1.25, sm: 2 },
                          py: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          boxShadow: 1,
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          columnGap: 2,
                          rowGap: 0.75,
                        }}
                      >
                        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.95rem',
                              lineHeight: 1.3,
                            }}
                          >
                            {selectedProfile.profileName ||
                              tplName ||
                              getProfileDisplay(selectedProfile)}
                          </Typography>
                          <Typography
                            noWrap
                            variant="caption"
                            color="text.secondary"
                            component="div"
                          >
                            {[
                              tplName,
                              selectedProfile.version > 1
                                ? `v${selectedProfile.version}`
                                : null,
                              assessmentTypeLabel(
                                selectedProfile.assessmentType,
                                language
                              ),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        </Box>
                        {canEdit && (
                          <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                display: 'block',
                                lineHeight: 1.5,
                              }}
                            >
                              {language === 'en'
                                ? `Assessed ${st.assessed} of ${st.items}`
                                : `Оценено ${st.assessed} из ${st.items}`}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={
                                st.items ? (st.assessed / st.items) * 100 : 0
                              }
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        )}
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.15rem',
                              lineHeight: 1.1,
                              color: `${pctColor(st.pct)}.main`,
                            }}
                          >
                            {st.pct}%
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {st.totalScore}/{st.maxScore}
                          </Typography>
                        </Box>
                        {isTeacher && autoSaveStatus && (
                          <Tooltip
                            title={
                              autoSaveStatus === 'error'
                                ? `${autoSaveError} — ${language === 'en' ? 'click to retry' : 'нажмите, чтобы повторить'}`
                                : ''
                            }
                          >
                            <Chip
                              size="small"
                              label={
                                autoSaveStatus === 'saving'
                                  ? language === 'en'
                                    ? 'Saving…'
                                    : 'Сохранение…'
                                  : autoSaveStatus === 'saved'
                                    ? language === 'en'
                                      ? 'Saved ✓'
                                      : 'Сохранено ✓'
                                    : autoSaveStatus === 'pending'
                                      ? language === 'en'
                                        ? 'Unsaved'
                                        : 'Не сохранено'
                                      : language === 'en'
                                        ? 'Error — Retry'
                                        : 'Ошибка — Повторить'
                              }
                              color={
                                autoSaveStatus === 'saved'
                                  ? 'success'
                                  : autoSaveStatus === 'error'
                                    ? 'error'
                                    : 'default'
                              }
                              variant={
                                autoSaveStatus === 'error'
                                  ? 'filled'
                                  : 'outlined'
                              }
                              onClick={
                                autoSaveStatus === 'error'
                                  ? retryAutoSave
                                  : undefined
                              }
                              sx={{ flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Session Notes */}
                  {isTeacher && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      size="small"
                      label={
                        language === 'en'
                          ? 'Session Notes'
                          : 'Заметки к занятию'
                      }
                      placeholder={
                        language === 'en'
                          ? 'How did the session go? Any observations...'
                          : 'Как прошло занятие? Наблюдения...'
                      }
                      value={sessionNotes}
                      disabled={viewingOldVersion}
                      onChange={(e) => {
                        const notes = e.target.value;
                        setSessionNotes(notes);
                        latestNotesRef.current = notes;
                        saveBackupToStorage(latestProfileRef.current, notes);
                        scheduleAutoSave(latestProfileRef.current, notes);
                      }}
                      sx={{ mb: 1.5 }}
                    />
                  )}
                  {isParent && selectedProfile.notes && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2">
                        {language === 'en'
                          ? 'Teacher Notes'
                          : 'Заметки учителя'}
                        :
                      </Typography>
                      {selectedProfile.notes}
                    </Alert>
                  )}

                  {/* Theme Filter Chips + Focus Mode + collapse all */}
                  {selectedProfile.themes.length > 1 && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <FilterIcon color="action" fontSize="small" />
                      <Chip
                        label={language === 'en' ? 'All' : 'Все'}
                        color={
                          activeThemeFilters.length === 0
                            ? 'primary'
                            : 'default'
                        }
                        onClick={() => setActiveThemeFilters([])}
                        size="small"
                      />
                      {selectedProfile.themes.map((theme, idx) => {
                        const { pct } = getThemeScore(theme);
                        return (
                          <Chip
                            key={idx}
                            label={`${theme.themeName} (${pct}%)`}
                            color={
                              activeThemeFilters.includes(theme.themeName)
                                ? 'primary'
                                : 'default'
                            }
                            variant={
                              activeThemeFilters.includes(theme.themeName)
                                ? 'filled'
                                : 'outlined'
                            }
                            onClick={() => {
                              setActiveThemeFilters((prev) =>
                                prev.includes(theme.themeName)
                                  ? prev.filter((n) => n !== theme.themeName)
                                  : [...prev, theme.themeName]
                              );
                            }}
                            size="small"
                            sx={{
                              borderLeftWidth: 3,
                              borderLeftStyle: 'solid',
                              borderLeftColor: `${pctColor(pct)}.main`,
                            }}
                          />
                        );
                      })}
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 0.5 }}
                      />
                      <Tooltip
                        title={
                          language === 'en'
                            ? 'Focus Mode: Hide mastered items'
                            : 'Фокус: Скрыть освоенные'
                        }
                      >
                        <Chip
                          icon={
                            focusMode ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )
                          }
                          label={language === 'en' ? 'Focus' : 'Фокус'}
                          color={focusMode ? 'warning' : 'default'}
                          onClick={() => setFocusMode(!focusMode)}
                          size="small"
                        />
                      </Tooltip>
                      {(() => {
                        const allCollapsed =
                          collapsedThemes.length >=
                          selectedProfile.themes.length;
                        return (
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={
                              allCollapsed ? (
                                <ExpandMoreIcon />
                              ) : (
                                <ExpandLessIcon />
                              )
                            }
                            label={
                              allCollapsed
                                ? language === 'en'
                                  ? 'Expand all'
                                  : 'Развернуть все'
                                : language === 'en'
                                  ? 'Collapse all'
                                  : 'Свернуть все'
                            }
                            onClick={() =>
                              setCollapsedThemes(
                                allCollapsed
                                  ? []
                                  : selectedProfile.themes.map((_, i) => i)
                              )
                            }
                          />
                        );
                      })()}
                    </Box>
                  )}
                  {canEdit && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: { xs: 'none', md: 'block' }, mb: 1 }}
                    >
                      {language === 'en'
                        ? 'Tip: click a row, then 0 · 1 · 2 to score, ↑ ↓ to move'
                        : 'Подсказка: выберите строку — 0 · 1 · 2 ставят оценку, ↑ ↓ — переход'}
                    </Typography>
                  )}

                  {/* Themes — collapsible sections with compact item rows */}
                  <Box ref={scoringRef}>
                    {getVisibleThemes().map((theme) => {
                      const realTIdx = selectedProfile.themes.indexOf(theme);
                      const { score, max, pct } = getThemeScore(theme);
                      const visibleItems = getVisibleItems(theme);

                      if (focusMode && visibleItems.length === 0) return null;

                      const collapsed = collapsedThemes.includes(realTIdx);
                      const groups = [
                        {
                          type: 'word',
                          label: language === 'en' ? 'Words' : 'Слова',
                          items: visibleItems.filter(
                            (i) => i.itemType !== 'phrase'
                          ),
                        },
                        {
                          type: 'phrase',
                          label: language === 'en' ? 'Phrases' : 'Фразы',
                          items: visibleItems.filter(
                            (i) => i.itemType === 'phrase'
                          ),
                        },
                      ].filter((g) => g.items.length > 0);

                      return (
                        <Box
                          key={realTIdx}
                          sx={{
                            mb: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                          }}
                        >
                          {/* Theme header */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              pr: 1,
                              bgcolor: 'action.hover',
                            }}
                          >
                            <ButtonBase
                              onClick={() => toggleThemeCollapsed(realTIdx)}
                              aria-expanded={!collapsed}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                                gap: 0.5,
                                pl: 1,
                                py: 0.75,
                                textAlign: 'left',
                              }}
                            >
                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{
                                  color: 'text.secondary',
                                  transform: collapsed
                                    ? 'rotate(-90deg)'
                                    : 'none',
                                  transition: 'transform 0.15s',
                                }}
                              />
                              <Typography
                                noWrap
                                sx={{ fontWeight: 700, fontSize: '0.92rem' }}
                              >
                                {theme.themeName}
                              </Typography>
                            </ButtonBase>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              color={pctColor(pct)}
                              sx={{
                                width: { xs: 40, sm: 90 },
                                height: 6,
                                borderRadius: 3,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                minWidth: 34,
                                textAlign: 'right',
                                color: `${pctColor(pct)}.main`,
                              }}
                            >
                              {pct}%
                            </Typography>
                            {canEdit && score < max && (
                              <Button
                                size="small"
                                color="success"
                                startIcon={
                                  <DoneAllIcon sx={{ fontSize: '16px' }} />
                                }
                                onClick={() =>
                                  handleMarkThemeMastered(realTIdx)
                                }
                                sx={{
                                  py: 0,
                                  px: 0.75,
                                  minWidth: 0,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {language === 'en'
                                  ? 'All mastered'
                                  : 'Все знает'}
                              </Button>
                            )}
                          </Box>

                          {/* Item rows */}
                          <Collapse in={!collapsed} unmountOnExit>
                            {groups.map((g) => (
                              <Box key={g.type}>
                                {groups.length > 1 && (
                                  <Typography
                                    sx={{
                                      px: 1.5,
                                      pt: 0.75,
                                      pb: 0.25,
                                      fontSize: '0.66rem',
                                      fontWeight: 700,
                                      letterSpacing: 0.6,
                                      textTransform: 'uppercase',
                                      color: 'text.disabled',
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {g.label}
                                  </Typography>
                                )}
                                {g.items.map((item) =>
                                  renderScoreRow(theme, realTIdx, item)
                                )}
                              </Box>
                            ))}
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: { xs: 'stretch', sm: 'flex-start' },
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={exportPDF}
                      startIcon={<DownloadIcon />}
                      sx={{
                        borderRadius: 2,
                        flex: { xs: '1 1 140px', sm: 'none' },
                      }}
                    >
                      {t.exportPdf}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setPdfPanelOpen(true)}
                      startIcon={<DownloadIcon />}
                      sx={{
                        borderRadius: 2,
                        flex: { xs: '1 1 140px', sm: 'none' },
                      }}
                    >
                      {language === 'en' ? 'Combined PDF' : 'Сводный PDF'}
                    </Button>
                    {isTeacher && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={generateAssignment}
                          sx={{
                            borderRadius: 2,
                            flex: { xs: '1 1 140px', sm: 'none' },
                          }}
                        >
                          {t.generateAssignment}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            borderRadius: 2,
                            bgcolor: 'secondary.main',
                            flex: { xs: '1 1 140px', sm: 'none' },
                          }}
                          startIcon={<BoltIcon />}
                          disabled={viewingOldVersion}
                          onClick={startSpeedMode}
                        >
                          {language === 'en' ? 'Speed mode' : 'Быстрый режим'}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {tabValue === 1 && (
            <ProgressComparison
              studentId={id}
              onReassessWeakThemes={isTeacher ? handleReassessWeakThemes : null}
            />
          )}
          {tabValue === 2 && (
            <StudentTimeline studentId={id} refreshKey={profilesVersion} />
          )}
          {tabValue === 3 && (
            <AttendanceImpact
              studentId={id}
              refreshKey={profilesVersion}
              attendance={student?.attendance}
            />
          )}
          {tabValue === 4 && (
            <MultimediaPortfolio studentId={id} isReadOnly={isParent} />
          )}
          {tabValue === 5 && (
            <HomeObservations
              studentId={id}
              observations={student?.homeObservations}
              isParent={isParent}
              onUpdate={loadStudent}
            />
          )}
          {tabValue === 6 && (
            <LessonNotes studentId={id} isReadOnly={isParent} />
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <BackToDashboardButton />
          </Box>
        </Card>

        {/* Speed Mode */}
        <FlashcardAssessment
          open={openSpeedMode && !!selectedProfile}
          onClose={() => setOpenSpeedMode(false)}
          profile={selectedProfile}
          profileId={selectedProfile?._id}
          initialThemeFilter={speedThemes}
          onSave={(newThemes) => {
            const newTotalScore = newThemes.reduce(
              (sum, t) => sum + t.items.reduce((s, i) => s + i.score, 0),
              0
            );
            const newMaxScore = newThemes.reduce(
              (sum, t) => sum + t.items.length * 2,
              0
            );
            setSelectedProfile((prev) =>
              prev
                ? {
                    ...prev,
                    themes: newThemes,
                    totalScore: newTotalScore,
                    maxScore: newMaxScore,
                  }
                : prev
            );
            setProfilesVersion((v) => v + 1);
            // Reload to get server-confirmed state
            loadProfiles();
          }}
        />

        {/* Theme Selection Dialog */}
        <Dialog
          open={themeSelectionOpen}
          onClose={() => setThemeSelectionOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {language === 'en'
              ? 'Select Themes to Assess'
              : 'Выберите темы для оценки'}
          </DialogTitle>
          <DialogContent>
            {(() => {
              // Same template already assessed → grow that assessment instead
              // of starting another one (keeps the list of assessments short)
              const existing = profiles
                .filter(
                  (p) =>
                    p.isLatest !== false &&
                    templateIdOf(p) === String(templateId)
                )
                .sort(
                  (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                )[0];
              if (!existing) return null;
              const missing = missingUnitsOf(existing).map((tt) =>
                String(tt._id)
              );
              const toAdd = selectedThemeIds
                .map(String)
                .filter((x) => missing.includes(x));
              return (
                <Alert
                  severity="info"
                  sx={{ mb: 2, borderRadius: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={!toAdd.length || addUnitsBusy}
                      onClick={async () => {
                        if (await addUnitsToProfile(existing, toAdd, false)) {
                          setThemeSelectionOpen(false);
                          setCreateFormOpen(false);
                          setSelectedThemeIds([]);
                        }
                      }}
                    >
                      {language === 'en' ? 'Add there' : 'Добавить туда'}
                    </Button>
                  }
                >
                  {language === 'en'
                    ? `This student already has «${existing.profileName}». Add the chosen units to it instead of a new assessment.`
                    : `У ученика уже есть оценка «${existing.profileName}». Можно добавить выбранные юниты в неё, а не создавать новую.`}
                  {selectedThemeIds.length > 0 && !toAdd.length && (
                    <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                      {language === 'en'
                        ? 'All chosen units are already in it.'
                        : 'Все выбранные юниты уже в ней.'}
                    </Box>
                  )}
                </Alert>
              );
            })()}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      selectedThemeIds.length ===
                      selectedTemplate?.themes?.length
                    }
                    onChange={(e) =>
                      setSelectedThemeIds(
                        e.target.checked
                          ? selectedTemplate.themes.map((t) => t._id)
                          : []
                      )
                    }
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 700 }}>
                    {language === 'en' ? 'Select All' : 'Выбрать все'}
                  </Typography>
                }
              />
              <Divider sx={{ my: 1 }} />
              {selectedTemplate?.themes?.map((theme) => (
                <FormControlLabel
                  key={theme._id}
                  control={
                    <Checkbox
                      checked={selectedThemeIds.includes(theme._id)}
                      onChange={(e) =>
                        setSelectedThemeIds((prev) =>
                          e.target.checked
                            ? [...prev, theme._id]
                            : prev.filter((id) => id !== theme._id)
                        )
                      }
                    />
                  }
                  label={(() => {
                    const n = theme.items?.length || 0;
                    return language === 'en'
                      ? `${theme.name} (${n} items)`
                      : `${theme.name} (${n} ${pluralRu(n, 'пункт', 'пункта', 'пунктов')})`;
                  })()}
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setThemeSelectionOpen(false)}>
              {language === 'en' ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              onClick={confirmThemeSelection}
              disabled={selectedThemeIds.length === 0}
            >
              {language === 'en'
                ? `Create (${selectedThemeIds.length} themes)`
                : `Создать (${selectedThemeIds.length} тем)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Version History Dialog */}
        <Dialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {language === 'en' ? 'Version History' : 'История версий'}
          </DialogTitle>
          <DialogContent>
            <List>
              {versionHistory.map((v, i) => (
                <ListItemButton
                  key={v._id}
                  divider
                  onClick={() => {
                    setHistoryOpen(false);
                    flushAutoSave();
                    // Prefer the list entry (populated template, fresh scores)
                    applySelectedProfile(
                      profiles.find((p) => p._id === v._id) || v
                    );
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemText
                    primary={`v${v.version} — ${v.profileName || new Date(v.createdAt).toLocaleDateString()}`}
                    secondary={`${assessmentTypeLabel(v.assessmentType, language)} | ${language === 'en' ? 'Score' : 'Балл'}: ${v.totalScore}/${v.maxScore} (${v.maxScore > 0 ? Math.round((v.totalScore / v.maxScore) * 100) : 0}%)`}
                  />
                  {v.isLatest && (
                    <Chip
                      label={language === 'en' ? 'Latest' : 'Последняя'}
                      size="small"
                      color="primary"
                    />
                  )}
                </ListItemButton>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryOpen(false)}>
              {language === 'en' ? 'Close' : 'Закрыть'}
            </Button>
          </DialogActions>
        </Dialog>

        <ShareAssessmentDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          profile={selectedProfile}
          en={language === 'en'}
        />

        {/* Reassess confirmation */}
        <Dialog
          open={!!reassessTarget}
          onClose={() => setReassessTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {language === 'en' ? 'Reassess' : 'Переоценка'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {language === 'en'
                ? `Create a new assessment version (v${(reassessTarget?.version || 1) + 1})? Current scores will be kept in history.`
                : `Создать новую версию оценки (v${(reassessTarget?.version || 1) + 1})? Текущие оценки сохранятся в истории.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setReassessTarget(null)}>
              {language === 'en' ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ReplayIcon />}
              disabled={loading || !reassessTarget}
              onClick={() => handleReassess(reassessTarget)}
            >
              {language === 'en' ? 'Create' : 'Создать'} v
              {(reassessTarget?.version || 1) + 1}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* ── Homework Dialog (3 tabs) ── */}
      <Dialog
        open={assignmentOpen}
        onClose={() => setAssignmentOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3, minHeight: 560 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 800,
            pb: 1.5,
          }}
        >
          <AssignmentIcon sx={{ color: '#d97706' }} />
          {language === 'en' ? 'Homework' : 'Домашнее задание'}
          {assignmentData?.studentName && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 0.5, fontWeight: 400 }}
            >
              — {assignmentData.studentName}
            </Typography>
          )}
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={hwTab} onChange={(_, v) => setHwTab(v)}>
            <Tab
              label="AI"
              icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              sx={{ fontSize: '0.82rem', minHeight: 40 }}
            />
            <Tab
              label={language === 'en' ? 'Template' : 'Шаблон'}
              icon={<AssignmentIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              sx={{ fontSize: '0.82rem', minHeight: 40 }}
            />
            <Tab
              label={language === 'en' ? 'Quick' : 'Быстро'}
              icon={<CopyIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              sx={{ fontSize: '0.82rem', minHeight: 40 }}
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          {/* ── Tab 0: AI Generate ─────────────────────────────────────── */}
          {hwTab === 0 && (
            <Box>
              {/* History bar */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mr: 0.5 }}
                >
                  {language === 'en' ? 'History:' : 'История:'}
                </Typography>
                {hwHistory.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {language === 'en'
                      ? 'No saved homeworks yet'
                      : 'Нет сохранённых заданий'}
                  </Typography>
                )}
                {hwHistory.map((h) => (
                  <Chip
                    key={h._id}
                    label={
                      new Date(h.createdAt).toLocaleDateString(
                        language === 'en' ? 'en-GB' : 'ru-RU',
                        { day: 'numeric', month: 'short' }
                      ) + (h.source === 'template' ? ' 📋' : ' ✨')
                    }
                    size="small"
                    variant={hwSelected?._id === h._id ? 'filled' : 'outlined'}
                    color={hwSelected?._id === h._id ? 'primary' : 'default'}
                    onClick={() => {
                      setHwSelected(h);
                      setHwText(h.content || '');
                      setHwStructured(structuredFor(h));
                      setHwEditMode(false);
                    }}
                    onDelete={() => hwDeleteItem(h._id)}
                    sx={{ fontSize: '0.72rem' }}
                  />
                ))}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={
                    hwGenerating ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  disabled={hwGenerating}
                  onClick={hwGenerate}
                  sx={{
                    ml: 'auto',
                    bgcolor: '#d97706',
                    '&:hover': { bgcolor: '#b45309' },
                  }}
                >
                  {hwGenerating
                    ? language === 'en'
                      ? 'Generating…'
                      : 'Генерация…'
                    : language === 'en'
                      ? 'Generate New'
                      : 'Сгенерировать'}
                </Button>
              </Box>

              {/* ── Structured preview OR raw textarea ── */}
              {(hwSelected || hwText) &&
              hwStructured &&
              !hwEditMode &&
              !hwDirty ? (
                /* ── Beautiful structured preview ── */
                <Box
                  sx={{
                    maxHeight: 390,
                    overflowY: 'auto',
                    pr: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'action.hover',
                  }}
                >
                  {/* Greeting */}
                  {hwStructured.greeting && (
                    <Typography
                      fontWeight={800}
                      sx={{ fontSize: '1rem', mb: 1, color: '#1c1917' }}
                    >
                      {hwStructured.greeting}
                    </Typography>
                  )}
                  {/* Intro */}
                  {hwStructured.intro && (
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        color: '#57534e',
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                      }}
                    >
                      {hwStructured.intro}
                    </Typography>
                  )}
                  {/* Per-theme cards */}
                  <Stack spacing={1.25} sx={{ mb: 2 }}>
                    {(hwStructured.themes || []).map((rawTheme, ti) => {
                      const aiTheme = rawTheme || {};
                      // Null-safe match to the local theme (AI may rename or omit the name)
                      const aiName = normName(aiTheme.name);
                      const td = aiName
                        ? assignmentData?.themeData?.find(
                            (t) => normName(t.name) === aiName
                          )
                        : null;
                      // Pills: local items when matched, otherwise the AI's own words
                      const fromAi = (list) =>
                        (Array.isArray(list) ? list : [])
                          .map((w) =>
                            typeof w === 'string'
                              ? { text: w }
                              : { text: w?.en || '', hint: w?.ru || '' }
                          )
                          .filter((p) => p.text);
                      const fromLocal = (items) =>
                        items.map((i) => ({
                          text: i.itemText,
                          phrase: i.itemType === 'phrase',
                        }));
                      const learn = td
                        ? fromLocal(td.needsWork)
                        : fromAi(aiTheme.learnWords);
                      const review = td
                        ? fromLocal(td.developing)
                        : fromAi(aiTheme.reviewWords);
                      const hasLearn = learn.length > 0 || !!aiTheme.learnTips;
                      const hasReview =
                        review.length > 0 || !!aiTheme.reviewTips;
                      const hasNew = learn.length > 0;
                      const accent = hasNew ? '#ef4444' : '#f59e0b';
                      const hdrBg = hasNew ? '#fef2f2' : '#fffbeb';
                      const bdColor = hasNew ? '#fca5a5' : '#fcd34d';
                      const renderPills = (pills, bg, border, color, mb) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            mb,
                          }}
                        >
                          {pills.map((p, i) => (
                            <Box
                              key={i}
                              sx={{
                                px: 1,
                                py: 0.3,
                                bgcolor: bg,
                                border: `1px solid ${border}`,
                                borderRadius: 1.5,
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color,
                                fontStyle: p.phrase ? 'italic' : 'normal',
                              }}
                            >
                              {p.text}
                              {p.hint && (
                                <Box
                                  component="span"
                                  sx={{ opacity: 0.7, fontWeight: 400 }}
                                >
                                  {' · '}
                                  {p.hint}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                      return (
                        <Paper
                          key={ti}
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: bdColor,
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          {/* Theme header */}
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.75,
                              bgcolor: hdrBg,
                              borderBottom: '1px solid',
                              borderColor: bdColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 3,
                                height: 14,
                                borderRadius: 1,
                                bgcolor: accent,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                flex: 1,
                                color: '#1c1917',
                              }}
                            >
                              {aiTheme.name ||
                                td?.name ||
                                `${language === 'en' ? 'Theme' : 'Тема'} ${ti + 1}`}
                            </Typography>
                            {learn.length + review.length > 0 && (
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  color: accent,
                                }}
                              >
                                {learn.length + review.length}{' '}
                                {language === 'en' ? 'items' : 'шт.'}
                              </Typography>
                            )}
                          </Box>
                          {/* Body */}
                          <Box sx={{ p: 1.25, bgcolor: 'background.paper' }}>
                            {aiTheme.commentary && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: hasLearn || hasReview ? 1 : 0,
                                  fontSize: '0.85rem',
                                  color: '#374151',
                                  lineHeight: 1.55,
                                }}
                              >
                                {aiTheme.commentary}
                              </Typography>
                            )}
                            {/* Learn pills + tip */}
                            {hasLearn && (
                              <Box sx={{ mb: hasReview ? 1 : 0 }}>
                                {learn.length > 0 && (
                                  <>
                                    <Typography
                                      sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: '#ef4444',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.4,
                                        mb: 0.5,
                                      }}
                                    >
                                      {language === 'en'
                                        ? 'Learn (new):'
                                        : 'Выучить (новое):'}
                                    </Typography>
                                    {renderPills(
                                      learn,
                                      '#fef2f2',
                                      '#fca5a5',
                                      '#7f1d1d',
                                      aiTheme.learnTips ? 0.75 : 0
                                    )}
                                  </>
                                )}
                                {aiTheme.learnTips && (
                                  <Box
                                    sx={{
                                      px: 1.25,
                                      py: 0.75,
                                      bgcolor: '#fff7ed',
                                      borderLeft: '3px solid #fb923c',
                                      borderRadius: '0 6px 6px 0',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: '0.8rem',
                                        color: '#7c2d12',
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {aiTheme.learnTips}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                            {/* Review pills + tip */}
                            {hasReview && (
                              <Box>
                                {review.length > 0 && (
                                  <>
                                    <Typography
                                      sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: '#d97706',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.4,
                                        mb: 0.5,
                                      }}
                                    >
                                      {language === 'en'
                                        ? 'Review:'
                                        : 'Закрепить:'}
                                    </Typography>
                                    {renderPills(
                                      review,
                                      '#fffbeb',
                                      '#fcd34d',
                                      '#78350f',
                                      aiTheme.reviewTips ? 0.75 : 0
                                    )}
                                  </>
                                )}
                                {aiTheme.reviewTips && (
                                  <Box
                                    sx={{
                                      px: 1.25,
                                      py: 0.75,
                                      bgcolor: '#fffbeb',
                                      borderLeft: '3px solid #fcd34d',
                                      borderRadius: '0 6px 6px 0',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: '0.8rem',
                                        color: '#78350f',
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {aiTheme.reviewTips}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                  {/* Games at home */}
                  {Array.isArray(hwStructured.activities) &&
                    hwStructured.activities.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={hwSectionSx}>
                          {language === 'en' ? 'Games at home' : 'Игры дома'}
                        </Typography>
                        <Stack spacing={0.75}>
                          {hwStructured.activities.map((a, i) => (
                            <Box
                              key={i}
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                bgcolor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: '#14532d',
                                  }}
                                >
                                  {a?.title}
                                </Typography>
                                {a?.minutes ? (
                                  <Typography
                                    sx={{
                                      fontSize: '0.72rem',
                                      fontWeight: 600,
                                      color: '#15803d',
                                    }}
                                  >
                                    {a.minutes}{' '}
                                    {language === 'en' ? 'min' : 'мин'}
                                  </Typography>
                                ) : null}
                              </Box>
                              {a?.howTo && (
                                <Typography
                                  sx={{
                                    fontSize: '0.8rem',
                                    color: '#374151',
                                    lineHeight: 1.5,
                                    mt: 0.25,
                                  }}
                                >
                                  {a.howTo}
                                </Typography>
                              )}
                              {Array.isArray(a?.words) &&
                                a.words.length > 0 && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    {a.words.map((w, wi) => (
                                      <Chip
                                        key={wi}
                                        label={
                                          typeof w === 'string' ? w : w?.en
                                        }
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.7rem',
                                          bgcolor: '#fff',
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  {/* Week plan */}
                  {Array.isArray(hwStructured.weekPlan) &&
                    hwStructured.weekPlan.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={hwSectionSx}>
                          {language === 'en' ? 'Week plan' : 'План на неделю'}
                        </Typography>
                        {hwStructured.weekPlan.map((d, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              gap: 1,
                              py: 0.4,
                              borderBottom:
                                i < hwStructured.weekPlan.length - 1
                                  ? '1px dashed #e5e7eb'
                                  : 'none',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: '#1c1917',
                                minWidth: 88,
                                flexShrink: 0,
                              }}
                            >
                              {d?.day}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                color: '#374151',
                                lineHeight: 1.5,
                              }}
                            >
                              {d?.task}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  {/* End-of-week checklist for parents */}
                  {Array.isArray(hwStructured.parentChecklist) &&
                    hwStructured.parentChecklist.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={hwSectionSx}>
                          {language === 'en'
                            ? 'End-of-week check'
                            : 'Проверьте в конце недели'}
                        </Typography>
                        {hwStructured.parentChecklist.map((c, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.75,
                              py: 0.25,
                            }}
                          >
                            <CheckBoxBlankIcon
                              sx={{ fontSize: 16, color: '#9ca3af', mt: '2px' }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                color: '#374151',
                                lineHeight: 1.5,
                              }}
                            >
                              {c}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  {/* Praise */}
                  {hwStructured.praise && (
                    <Alert
                      severity="success"
                      icon={false}
                      sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.85rem' }}
                    >
                      {hwStructured.praise}
                    </Alert>
                  )}
                  {/* Closing + signature */}
                  {hwStructured.closing && (
                    <Typography
                      variant="body2"
                      sx={{ color: '#374151', mb: 0.5, lineHeight: 1.6 }}
                    >
                      {hwStructured.closing}
                    </Typography>
                  )}
                  {hwStructured.signature && (
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: '#1c1917' }}
                    >
                      {hwStructured.signature}
                    </Typography>
                  )}
                </Box>
              ) : hwSelected || hwText ? (
                /* ── Raw textarea ── */
                <TextField
                  multiline
                  fullWidth
                  minRows={14}
                  maxRows={22}
                  value={hwText}
                  onChange={(e) => setHwText(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.88rem',
                      lineHeight: 1.6,
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 260,
                    gap: 2,
                    color: 'text.secondary',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <AutoAwesomeIcon
                    sx={{ fontSize: 48, color: '#d97706', opacity: 0.6 }}
                  />
                  <Typography variant="body2">
                    {language === 'en'
                      ? 'Click "Generate New" to create an AI homework based on this profile'
                      : 'Нажмите "Сгенерировать" для создания задания на основе этого профиля'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={
                      hwGenerating ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    disabled={hwGenerating}
                    onClick={hwGenerate}
                    sx={{
                      bgcolor: '#d97706',
                      '&:hover': { bgcolor: '#b45309' },
                    }}
                  >
                    {hwGenerating
                      ? language === 'en'
                        ? 'Generating…'
                        : 'Генерация…'
                      : language === 'en'
                        ? 'Generate'
                        : 'Сгенерировать'}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 1: Template ────────────────────────────────────────── */}
          {hwTab === 1 && (
            <Box>
              <Alert
                severity="info"
                sx={{ mb: 1.5, fontSize: '0.8rem', py: 0.5 }}
              >
                {language === 'en'
                  ? 'This is a direct text template — variables are replaced automatically, no AI needed. Available: {studentName} {teacherName} {date} {themeNames} {weakItems} {developingItems} {masteredItems} {score}'
                  : 'Прямой текстовый шаблон — переменные подставляются автоматически, без AI. Доступны: {studentName} {teacherName} {date} {themeNames} {weakItems} {developingItems} {masteredItems} {score}'}
              </Alert>
              <TextField
                multiline
                fullWidth
                minRows={14}
                maxRows={22}
                value={hwTplText}
                onChange={(e) => setHwTplText(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.88rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    hwTplSaving ? <CircularProgress size={14} /> : <SaveIcon />
                  }
                  disabled={hwTplSaving}
                  onClick={hwSaveDefaultTemplate}
                >
                  {language === 'en'
                    ? 'Save as my default template'
                    : 'Сохранить как шаблон по умолчанию'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={
                    hwTplSaving ? <CircularProgress size={14} /> : <AddIcon />
                  }
                  disabled={hwTplSaving || !hwTplText.trim()}
                  onClick={() => hwSaveTemplate(assignmentData)}
                >
                  {language === 'en'
                    ? 'Save to history'
                    : 'Сохранить в историю'}
                </Button>
              </Stack>
            </Box>
          )}

          {/* ── Tab 2: Quick ───────────────────────────────────────────── */}
          {hwTab === 2 &&
            assignmentData &&
            (() => {
              const activeThemes = assignmentData.themeData.filter(
                (t) => t.needsWork.length > 0 || t.developing.length > 0
              );
              const totalWeak = activeThemes.reduce(
                (s, t) => s + t.needsWork.length,
                0
              );
              const totalDev = activeThemes.reduce(
                (s, t) => s + t.developing.length,
                0
              );
              return (
                <Box>
                  {/* ── Summary bar ── */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 2,
                      background:
                        'linear-gradient(135deg, #fff7ed 0%, #fef2f2 100%)',
                      border: '1px solid #fde68a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        noWrap
                        sx={{ color: '#1c1917' }}
                      >
                        {assignmentData.studentName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: '#78716c' }}
                        noWrap
                      >
                        {assignmentData.date.toLocaleDateString(
                          language === 'en' ? 'en-GB' : 'ru-RU',
                          { day: 'numeric', month: 'long', year: 'numeric' }
                        )}
                        {assignmentData.profileName
                          ? `  ·  ${assignmentData.profileName}`
                          : ''}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {totalWeak > 0 && (
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 99,
                            bgcolor: '#fef2f2',
                            border: '1px solid #fca5a5',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: '#991b1b',
                            lineHeight: 1.4,
                          }}
                        >
                          {language === 'en'
                            ? `${totalWeak} to learn`
                            : `${totalWeak} выучить`}
                        </Box>
                      )}
                      {totalDev > 0 && (
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 99,
                            bgcolor: '#fffbeb',
                            border: '1px solid #fcd34d',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: '#92400e',
                            lineHeight: 1.4,
                          }}
                        >
                          {language === 'en'
                            ? `${totalDev} review`
                            : `${totalDev} повторить`}
                        </Box>
                      )}
                      {assignmentData.totalMastered > 0 && (
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 99,
                            bgcolor: '#f0fdf4',
                            border: '1px solid #86efac',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: '#14532d',
                            lineHeight: 1.4,
                          }}
                        >
                          {language === 'en'
                            ? `${assignmentData.totalMastered} mastered`
                            : `${assignmentData.totalMastered} знает`}
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {activeThemes.length === 0 ? (
                    <Alert
                      severity="success"
                      icon={false}
                      sx={{ borderRadius: 2 }}
                    >
                      {language === 'en'
                        ? 'All items mastered — no homework needed!'
                        : 'Всё усвоено — домашнее задание не требуется!'}
                    </Alert>
                  ) : (
                    <Stack
                      spacing={1.25}
                      sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}
                    >
                      {activeThemes.map((theme, ti) => {
                        const hasNew = theme.needsWork.length > 0;
                        const accentColor = hasNew ? '#ef4444' : '#f59e0b';
                        const headerBg = hasNew ? '#fef2f2' : '#fffbeb';
                        const borderColor = hasNew ? '#fca5a5' : '#fcd34d';
                        return (
                          <Paper
                            key={ti}
                            elevation={0}
                            sx={{
                              border: '1px solid',
                              borderColor,
                              borderRadius: 2,
                              overflow: 'hidden',
                            }}
                          >
                            {/* Theme header */}
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                bgcolor: headerBg,
                                borderBottom: '1px solid',
                                borderColor,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 3,
                                  height: 14,
                                  borderRadius: 1,
                                  bgcolor: accentColor,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 800,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  color: '#1c1917',
                                  flex: 1,
                                }}
                              >
                                {theme.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  color: accentColor,
                                }}
                              >
                                {theme.needsWork.length +
                                  theme.developing.length}{' '}
                                {language === 'en' ? 'items' : 'шт.'}
                              </Typography>
                            </Box>

                            {/* Items body */}
                            <Box sx={{ p: 1.25, bgcolor: 'background.paper' }}>
                              {theme.needsWork.length > 0 && (
                                <Box
                                  sx={{
                                    mb: theme.developing.length > 0 ? 1 : 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      color: '#ef4444',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.4,
                                      mb: 0.6,
                                    }}
                                  >
                                    {language === 'en'
                                      ? 'Learn (new):'
                                      : 'Выучить (новое):'}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                    }}
                                  >
                                    {theme.needsWork.map((item, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          px: 1,
                                          py: 0.3,
                                          bgcolor: '#fef2f2',
                                          border: '1px solid #fca5a5',
                                          borderRadius: 1.5,
                                          fontSize: '0.82rem',
                                          fontWeight: 500,
                                          color: '#7f1d1d',
                                          fontStyle:
                                            item.itemType === 'phrase'
                                              ? 'italic'
                                              : 'normal',
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {item.itemText}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {theme.developing.length > 0 && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      color: '#d97706',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.4,
                                      mb: 0.6,
                                    }}
                                  >
                                    {language === 'en'
                                      ? 'Review (consolidate):'
                                      : 'Закрепить (повторить):'}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                    }}
                                  >
                                    {theme.developing.map((item, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          px: 1,
                                          py: 0.3,
                                          bgcolor: '#fffbeb',
                                          border: '1px solid #fcd34d',
                                          borderRadius: 1.5,
                                          fontSize: '0.82rem',
                                          fontWeight: 500,
                                          color: '#78350f',
                                          fontStyle:
                                            item.itemType === 'phrase'
                                              ? 'italic'
                                              : 'normal',
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {item.itemText}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              );
            })()}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Button onClick={() => setAssignmentOpen(false)} sx={{ mr: 'auto' }}>
            {language === 'en' ? 'Close' : 'Закрыть'}
          </Button>

          {/* Tab 0 actions */}
          {hwTab === 0 && hwSelected && (
            <>
              {hwStructured && !hwDirty && (
                <Button
                  size="small"
                  startIcon={hwEditMode ? <VisibilityIcon /> : <EditIcon />}
                  onClick={() => setHwEditMode((m) => !m)}
                  sx={{ mr: 'auto' }}
                >
                  {hwEditMode
                    ? language === 'en'
                      ? 'Preview'
                      : 'Предпросмотр'
                    : language === 'en'
                      ? 'Edit text'
                      : 'Редактировать'}
                </Button>
              )}
              <Button
                size="small"
                startIcon={
                  hwSaving ? <CircularProgress size={14} /> : <SaveIcon />
                }
                disabled={hwSaving || !hwDirty}
                onClick={hwSaveEdits}
              >
                {language === 'en' ? 'Save edits' : 'Сохранить'}
              </Button>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={hwResetItem}
                disabled={
                  hwSaving ||
                  hwSelected.originalContent == null ||
                  (!hwDirty &&
                    !hwSelected.edited &&
                    hwSelected.content === hwSelected.originalContent)
                }
              >
                {language === 'en' ? 'Reset' : 'Сброс'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TelegramIcon />}
                onClick={() => hwSendTelegram(hwSelected)}
                disabled={hwSaving || hwSelected.status === 'sent'}
              >
                Telegram
              </Button>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(hwText).catch(() => {});
                  setHwSnack({
                    open: true,
                    message: language === 'en' ? 'Copied!' : 'Скопировано!',
                    severity: 'success',
                  });
                }}
              >
                {language === 'en' ? 'Copy' : 'Копировать'}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  hwPdfLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={hwPdfLoading}
                onClick={() =>
                  // Edited text → no AI structure, PDF renders `content`
                  hwDownloadPDF(
                    hwText,
                    assignmentData,
                    hwDirty ? null : hwStructured
                  )
                }
                sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
              >
                PDF
              </Button>
            </>
          )}

          {/* Tab 1 actions */}
          {hwTab === 1 && hwTplText && (
            <>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(hwTplText).catch(() => {});
                  setHwSnack({
                    open: true,
                    message: language === 'en' ? 'Copied!' : 'Скопировано!',
                    severity: 'success',
                  });
                }}
              >
                {language === 'en' ? 'Copy' : 'Копировать'}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  hwPdfLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={hwPdfLoading}
                onClick={() => hwDownloadPDF(hwTplText, assignmentData)}
                sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
              >
                PDF
              </Button>
            </>
          )}

          {/* Tab 2 actions */}
          {hwTab === 2 && assignmentData && (
            <>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                variant="outlined"
                onClick={() => {
                  copyAssignment(assignmentData);
                  setHwSnack({
                    open: true,
                    message: language === 'en' ? 'Copied!' : 'Скопировано!',
                    severity: 'success',
                  });
                }}
              >
                {language === 'en' ? 'Copy' : 'Копировать'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadAssignment(assignmentData)}
              >
                .txt
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  hwPdfLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={hwPdfLoading}
                onClick={() =>
                  hwDownloadPDF(
                    buildAssignmentText(assignmentData),
                    assignmentData
                  )
                }
                sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
              >
                PDF
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Page snackbar (homework + profile messages) */}
      <Snackbar
        key={hwSnack.message}
        open={hwSnack.open}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway')
            setHwSnack((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={hwSnack.severity}
          onClose={() => setHwSnack((s) => ({ ...s, open: false }))}
          sx={{ boxShadow: 4, borderRadius: 2, minWidth: 240 }}
        >
          {hwSnack.message}
        </Alert>
      </Snackbar>
      <PdfExportPanel
        open={pdfPanelOpen}
        onClose={() => setPdfPanelOpen(false)}
        language={language}
        initialStudentId={student?._id || ''}
        defaultMode="combined"
      />
    </Layout>
  );
}
